/**
 * main.ts — BridgeViewport interop surface
 * -----------------------------------------------------------------------------
 * Vite builds this as an IIFE that attaches the exported functions to
 * `window.BridgeViewport` (see vite.config.ts `lib.name`). Blazor drives it
 * entirely through IJSRuntime:
 *
 *   await JS.InvokeVoidAsync("BridgeViewport.init", "modeler-view-1");
 *   await JS.InvokeVoidAsync("BridgeViewport.load", "modeler-view-1", jsonString);
 *   await JS.InvokeVoidAsync("BridgeViewport.setView", "modeler-view-1", "iso");
 *
 * MULTI-INSTANCE: every 3D viewport is keyed by its container element id, so the
 * Modeler can show several live 3D views side by side (and tear them off into
 * their own windows). Each exported 3D function takes that id as its first arg
 * and dispatches to the matching {@link Instance}. `init(id)` is idempotent —
 * calling it again disposes the previous instance for that id first.
 *
 * (The 2D preview API — mount2D/update2D/zoom2D/unmount2D — was already keyed by
 * host id and is unchanged.)
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import helvetikerBold from 'three/examples/fonts/helvetiker_bold.typeface.json';
import { Viewport3D } from './Viewport3D';

// Real vector font for the 3D text meshes — parsed once, bundled (not a bitmap).
const LABEL_FONT: Font = new FontLoader().parse(helvetikerBold as unknown as Parameters<FontLoader['parse']>[0]);
import { BridgeScene, type BridgeGeometryPayload } from './BridgeScene';
import { FEScene, type FEPayload } from './FEMesh';
import { FEAnimator, type DisplacementField } from './FEAnimator';
import { ReactionArrows, arrowGeom, type ReactionArrowsPayload } from './ReactionArrows';
import { Hud } from './Hud';
import { ColorScale } from './ColorScale';
import { ClippingPanel } from './ClippingPanel';
import { Canvas2DRenderer } from './Canvas2DRenderer';
import { svgToPage } from './io/SvgImport';
import {
  buildLandXmlScene, buildSceneFromDoc, DEFAULT_DISPLAY,
  type ElevationRange, type LandXmlDisplayOptions,
} from './io/LandXmlScene';
import {
  DEFAULT_TERRAIN, EARTH_COLOR, WIREFRAME_COLOR, affordableSubdivisions,
  rampFor, shapeTerrain, wantedPasses, type TerrainDisplay,
} from './io/TerrainDisplay';
import {
  buildVegetation, disposeVegetation, foliagePending, refreshFoliageTextures,
  skyBackground, skyHorizonHex,
} from './io/Vegetation';
import type { LandXmlDoc } from './landxml/types';
import { buildMapGrid, type SurveyFrame } from './io/LandXmlGrid';
import { prefersDark, watchTheme } from './theme';
import type { DrawingPage } from './io/DrawingTypes';
import { pageToSvg, pageToDxf } from './io/DrawingExport';
import { exportShareMesh as writeShareMesh, type ShareExportStats } from './io/ShareExport';
import { qrSvg as makeQrSvg } from './io/QrSvg';
import { parseMesh as readMeshFile, MESH_FORMATS } from './io/MeshImport';
import { MeshGizmo, type GizmoMode, type MeshPose } from './MeshGizmo';

// Theme-aware canvas background. The dark background is the "sky" set by
// AddDefaultViewportEnvironment (captured at init); light mode uses a flat
// white. Tracking the WebView's prefers-color-scheme keeps the canvas in sync
// with the native title-bar theme toggle.
const LIGHT_BG = 0xffffff; // white background in light mode
/** Shipped fog density (Viewport3D's AddDefaultViewportEnvironment): 98% clear at
 *  200 m, which is right for a bridge and wrong for a square kilometre of terrain.
 *  See `fitEnvironmentToContent`. */
const DEFAULT_FOG_DENSITY = 0.0006;

/** Survey graticule colours, [light, dark]. Minor lines are deliberately close to
 *  the background: the grid is there to be referred to, not looked at. */
const MAP_GRID_COLORS = {
  minor: [0xc9d1d7, 0x3c454d],
  major: [0x93a1ab, 0x5b6873],
  label: [0x5d6871, 0x93a1ab],
} as const;
const LIGHT_FOG = 0xffffff; // fade distance to the background in light mode
const DARK_FOG = 0x2e3540; // matches AddDefaultViewportEnvironment's default

/** Named camera directions for the page's view-cube style toolbar buttons. */
export type ViewName = 'iso' | 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';

/** Fallback bounds when nothing is loaded yet — shared by setView and setClipping. */
/** Frame diagram value labels, relative to makeTextSprite's size — the default of the
 *  3D toolbar's Settings ▸ Diagram label scale. */
const DIAGRAM_LABEL_SCALE = 0.5;
const DEFAULT_BOUNDS =new THREE.Box3(new THREE.Vector3(-10, -2, -10), new THREE.Vector3(10, 2, 10));

/**
 * The isometric "home" viewing direction — camera offset from the model centre,
 * matching the reference home screenshot: measured live by orbiting the view to
 * match it and reading it back via BridgeViewport.debugCamera(): azimuth -60°,
 * elevation 25° off horizontal. Azimuth is measured from +Z toward +X, matching
 * camera-controls' internal spherical convention (see setView's `front`, at
 * [0, *, 1] — azimuth 0).
 *
 * Module-level because two things frame from it: the Isometric standard view
 * and the saved-project thumbnail (`captureThumbnail`), which must look like
 * the model's home view rather than wherever the user last orbited to.
 */
const ISO_DIR: [number, number, number] = (() => {
  const azimuth = THREE.MathUtils.degToRad(-60);
  const phi = Math.PI / 2 - THREE.MathUtils.degToRad(25);
  return [
    Math.sin(phi) * Math.sin(azimuth),
    Math.cos(phi),
    Math.sin(phi) * Math.cos(azimuth),
  ];
})();

type PickRef = { invokeMethodAsync(method: string, ...args: unknown[]): Promise<unknown> } | null;

interface LabelDef { text: string; kind?: string; x: number; y: number; z: number; dx?: number; dz?: number; }
interface TextItem { text: string; x: number; y: number; z: number; dx: number; dz: number; fill: string; size: number; }
interface DatumPayload {
  deckY?: number; groundY?: number;
  start?: [number, number, number]; dir?: [number, number];
  stationLabel?: string; bearingLabel?: string; deckLabel?: string; groundLabel?: string;
}

const LABEL_H = 2.6 * 0.2; // label text height (0.2× the previous size), metres
const LABEL_FILL: Record<string, string> = {
  support: '#363b3f',
  station: '#474d52',
  dim: '#474d52',
};

/**
 * One 3D viewport bound to a single container element. Holds all the state that
 * used to be module-global so several viewports can coexist.
 */
class Instance {
  hostId: string;
  viewport: Viewport3D;
  bridge: BridgeScene;
  fe: FEScene;
  feActive = false;
  reactionArrows: ReactionArrows;
  hud: Hud;
  colorScale: ColorScale;
  clip: ClippingPanel;
  feAnim: FEAnimator;

  darkBackground: THREE.Color | THREE.Texture | null = null;
  /** Unhooks the media-query + data-theme listeners (see theme.ts). */
  themeOff: (() => void) | null = null;

  // Last standard view picked via setView — drives the "only Isometric
  // rotates in Ortho mode" rule (see syncRotationLock).
  currentView: ViewName = 'iso';

  grid: THREE.GridHelper | null = null;
  /** Survey graticule under an imported LandXML map (io/LandXmlGrid.ts). Kept
   *  apart from `grid`, which is the bridge viewport's plain metre grid. */
  mapGrid: THREE.Group | null = null;
  /** Extent of `mapGrid`, folded into currentBounds() so framing includes it. */
  mapGridBox: THREE.Box3 | null = null;
  /** The parsed LandXML document behind the current map, kept so the Wireframe /
   *  Subdivide controls can rebuild the scene without re-reading the file. */
  mapDoc: LandXmlDoc | null = null;
  mapDisplay: LandXmlDisplayOptions = { ...DEFAULT_DISPLAY };
  /** Terrain options for a BRIDGE payload (the Modeler's Ground Profile), and the
   *  last payload they were applied to so a toolbar click can re-shape it without
   *  a round trip to the exporter. */
  terrainDisplay: TerrainDisplay = { ...DEFAULT_TERRAIN };
  lastPayload: BridgeGeometryPayload | null = null;
  /** Home ▸ Show Selected Only: the part ids left drawn, or null to draw everything.
   *  Applied to every payload, so a re-export while it is on stays isolated. */
  isolateIds: Set<string> | null = null;
  /** Trees and grass. Held on the scene, never in bridge.root — scenery must not
   *  reach picking, selection or the bounds Zoom All frames. */
  vegetation: THREE.Group | null = null;
  axes: THREE.Object3D | null = null;
  labelsGroup: THREE.Group | null = null;
  labelsVisible = false;
  datumGroup: THREE.Group | null = null;

  fePickRef: PickRef = null;
  fePickWired = false;
  downX = 0; downY = 0; downT = 0;

  // Element Results "Play": FEAnimator computes each frame; this instance just
  // ping-pongs through the precomputed (field, scale) pairs via
  // requestAnimationFrame — no interop at all once playFE*Animation has run.
  private animFrames: Array<{ field: DisplacementField; scale: number }> = [];
  private animIndex = 0;
  private animDir = 1;
  private animIntervalMs = 100;
  private animLastTick = 0;
  private animRafId: number | null = null;
  private readonly animStep = (now: number): void => {
    if (!this.animFrames.length) return;
    if (now - this.animLastTick >= this.animIntervalMs) {
      this.animLastTick = now;
      this.animIndex += this.animDir;
      if (this.animIndex >= this.animFrames.length) { this.animDir = -1; this.animIndex = Math.max(0, this.animFrames.length - 2); }
      else if (this.animIndex < 0) { this.animDir = 1; this.animIndex = Math.min(this.animFrames.length - 1, 1); }
      const f = this.animFrames[this.animIndex];
      this.feAnim.applyFrame(f.field, f.scale);
      this.viewport.invalidate();
    }
    this.animRafId = requestAnimationFrame(this.animStep);
  };

  bridgePickRef: PickRef = null;
  bridgePickWired = false;
  /** The last click's hit on the bridge (point and normal, world space), for lastPick(). */
  lastPickHit: { id: string; point: [number, number, number]; normal: [number, number, number] } | null = null;
  /** Host markers (setMarkers): spheres pinned to the surface; clicking one reports 'OnMarkerPicked'. */
  private markerGroup: THREE.Group | null = null;
  private readonly markerIds = new Map<string, string>();
  markerPickRef: PickRef = null;
  bridgeSelectMode = false;
  bDownX = 0; bDownY = 0; bDownT = 0;
  /** Move / rotate / scale handles on a selected custom mesh — made on first use. */
  private meshGizmo: MeshGizmo | null = null;

  // Notified when the clipping panel's own close button is clicked, so the
  // Blazor toolbar's toggle button can un-highlight itself (see setClipping).
  clipCloseRef: PickRef = null;

  private readonly fePicker = new THREE.Raycaster();
  private readonly feNdc = new THREE.Vector2();

  constructor(el: HTMLElement, containerId: string) {
    this.hostId = containerId;
    this.viewport = new Viewport3D(el, {
      // Slightly lighter than the modeler's default so the pale concrete
      // template geometry reads well against it.
      backgroundColor: 0x2b2f33,
      cameraFov: 35,
      // No floating in-canvas overlay: the pane's 3D-view toolbar (BridgeViewport3D)
      // drives all zoom / view / projection / rotate controls via interop.
      addToolbar: false,
    });
    this.bridge = new BridgeScene(this.viewport);
    this.fe = new FEScene(this.viewport);
    this.reactionArrows = new ReactionArrows(this.viewport);
    this.feAnim = new FEAnimator(this.fe);
    this.viewport.SetZoomAllTarget(this.bridge.root);
    // Frame the Zoom-All button on the live geometry bounds when present (the FE
    // analysis mesh when it's the active view, otherwise the bridge).
    this.viewport.zoomAllBox = () => {
      if (this.feActive && this.fe.hasContent) return this.currentBounds();
      return this.bridge.hasContent ? this.bridge.getBounds() : null;
    };

    // Bottom-right performance HUD (FPS · CPU · WebGL).
    this.hud = new Hud(this.viewport);

    // Bottom-center contour legend (Results ▸ "Disp. Color Dir."), hidden until set.
    this.colorScale = new ColorScale(this.viewport);

    // Bottom-center section-clipping card (X/Y/Z crop-box sliders), hidden until toggled on.
    // onClose notifies the Blazor toolbar's toggle button (if registered) so it
    // un-highlights when the panel is dismissed via its own close button rather
    // than the toolbar.
    this.clip = new ClippingPanel(this.viewport, () => {
      void this.clipCloseRef?.invokeMethodAsync('OnClippingClosed');
    });

    // Capture the default (dark) sky background, then apply the current theme and
    // keep it in sync with the WebView color scheme.
    this.darkBackground = this.viewport.scene.background as THREE.Color | THREE.Texture;
    this.applyTheme();
    this.themeOff = watchTheme(() => this.applyTheme());

    // Rotate-about-a-picked-point is the app's default rotation mode (the
    // toolbar's crosshair button starts lit): a click on a surface re-pivots
    // the camera there, so orbiting feels local to whatever you're looking at
    // instead of always swinging around the whole model's centre. Global
    // rotate is still one toolbar click away. Armed here rather than from the
    // Blazor toolbar because the host page owns init(), not the toolbar
    // component — see BridgeViewport3D.razor's _rotatePoint, which mirrors
    // this as the initial button highlight.
    this.viewport.setOrbitPointMode(true);
  }

  private applyTheme(): void {
    const dark = prefersDark();
    // A planted site is outdoors, so it gets a sky and a horizon-matched fog
    // instead of the neutral backdrop a bridge on bare ground wants.
    const outdoors = this.terrainDisplay.vegetation > 0 && this.vegetation !== null;

    this.viewport.scene.background = outdoors
      ? skyBackground(dark)
      : dark
        ? (this.darkBackground ?? new THREE.Color(0x2b2f33))
        : new THREE.Color(LIGHT_BG);

    const fog = this.viewport.scene.fog as THREE.FogExp2 | null;
    if (fog && fog.color) {
      fog.color.setHex(outdoors ? skyHorizonHex(dark) : dark ? DARK_FOG : LIGHT_FOG);
    }
    this.applyMapGridTheme(dark);
    this.viewport.invalidate();
  }

  /** Repaint the graticule for the current scheme — it is drawn in flat colours
   *  with no lighting, so nothing else would move it off the background. */
  private applyMapGridTheme(dark: boolean): void {
    if (!this.mapGrid) return;
    for (const child of this.mapGrid.children) {
      const role = child.userData.gridRole as keyof typeof MAP_GRID_COLORS | undefined;
      if (!role) continue;
      const mat = (child as THREE.LineSegments | THREE.Mesh).material as THREE.Material & {
        color?: THREE.Color; vertexColors?: boolean;
      };
      // The label mesh's vertex colours are white on purpose, so the material
      // tint below themes it exactly like the lines.
      mat.color?.setHex(MAP_GRID_COLORS[role][dark ? 1 : 0]);
    }
  }

  load(json: string, keepView = false): void {
    // A bridge payload has no survey frame, so any graticule from a previous
    // LandXML load in this viewport no longer means anything.
    this.clearMapGrid();
    let payload: BridgeGeometryPayload;
    try {
      payload = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse geometry payload', e);
      return;
    }
    this.lastPayload = payload;
    this.applyBridgeGeometry(payload);
    if (this.clip.isVisible) this.clip.setBounds(this.currentBounds() ?? DEFAULT_BOUNDS);
    this.viewport.invalidate();
    if (!keepView) this.frame(false);
  }

  /**
   * Shape any ground in a BRIDGE payload, then hand it to the scene.
   *
   * The Modeler's terrain arrives as ordinary surface parts flagged `terrain` by
   * BridgeGeometryExporter — there is no TIN here and no survey frame, just
   * triangles that happen to be ground. Everything else in the payload passes
   * through untouched, so the bridge itself is never reshaded or subdivided.
   */
  private applyBridgeGeometry(payload: BridgeGeometryPayload): void {
    // Show Selected Only keeps the parts the selection names — surfaces, their
    // outline edges and bars alike, since all carry the same object id. Id-less
    // parts (ground, diagram outlines) belong to nothing selectable and go.
    const keep = this.isolateIds;
    if (keep) payload = { ...payload, parts: (payload.parts ?? []).filter((p) => !!p.id && keep.has(p.id)) };

    // Never wireframed here, whatever the terrain options say. On the LandXML side the edges
    // show a TIN's own triangulation, which is survey information and worth seeing. A bridge
    // payload has none: its ground is the profile band, whose triangles come from the alignment
    // path interval and mean nothing about the site. Ruling it just combs the band — and being
    // ON by DEFAULT_TERRAIN, that is what it did as soon as the band joined the terrain group.
    const opts: TerrainDisplay = { ...this.terrainDisplay, wireframe: false };
    const ground = (payload.parts ?? []).filter(
      (p) => p.terrain && (p.kind ?? 'surface') === 'surface' && p.positions?.length >= 9,
    );

    if (!ground.length) {
      this.bridge.setGeometry(payload);
      this.applyElevationLegend(undefined);
      this.plant([]);
      return;
    }

    if (!opts.elevation && !opts.wireframe && opts.subdivide <= 0) {
      // Unshaded ground still goes to soil once it is planted — grass reads as
      // grass against earth, not against the default grey-blue.
      const parts = opts.vegetation > 0
        ? (payload.parts ?? []).map((p) => (p.terrain ? { ...p, color: EARTH_COLOR } : p))
        : payload.parts;
      this.bridge.setGeometry({ ...payload, parts });
      this.applyElevationLegend(undefined);
      this.plant(ground.map((p) => Float32Array.from(p.positions)));
      return;
    }

    // One height range across all the ground, so two surfaces at different levels
    // stay comparable, and one pass count for the same reason the LandXML side
    // has one — see MAX_SUBDIVIDED_FACES.
    let loY = Infinity, hiY = -Infinity, faces = 0;
    for (const p of ground) {
      faces += p.positions.length / 9;
      for (let i = 1; i < p.positions.length; i += 3) {
        const y = p.positions[i];
        if (y < loY) loY = y;
        if (y > hiY) hiY = y;
      }
    }
    const passes = affordableSubdivisions(faces, wantedPasses(opts.subdivide));

    const parts = [...(payload.parts ?? [])];
    const shapedGround: Float32Array[] = [];
    for (const p of ground) {
      const shaped = shapeTerrain(Float32Array.from(p.positions), passes, opts, loY, hiY);
      shapedGround.push(shaped.positions);
      const at = parts.indexOf(p);
      parts[at] = {
        ...p,
        color: opts.vegetation > 0 ? EARTH_COLOR : p.color,
        positions: Array.from(shaped.positions),
        vcolors: shaped.vcolors,
      };
      if (shaped.edges) {
        parts.push({
          name: `${p.name} (edges)`,
          color: WIREFRAME_COLOR,
          opacity: 1,
          kind: 'lines',
          positions: Array.from(shaped.edges),
        });
      }
    }

    this.bridge.setGeometry({ ...payload, parts });
    this.applyElevationLegend(
      opts.elevation && hiY - loY > 1e-9 ? { min: loY, max: hiY, unit: 'meter' } : undefined,
    );
    // Planted on the SHAPED ground, so trees sit on the surface the user can see
    // rather than hovering over (or sunk into) a smoothed one.
    this.plant(shapedGround);
  }

  /**
   * Redraw when the tree textures land — see Vegetation.foliagePending. The
   * viewport renders on demand, so without this the leaves stay invisible after
   * a planting until something else happens to invalidate. Bounded, so a texture
   * that never arrives cannot leave a poll running for the session.
   */
  private redrawWhenFoliageArrives(): void {
    if (!foliagePending()) return;
    let frames = 0;
    const tick = () => {
      if (!this.vegetation || frames++ > 300) return;   // ~5 s, then give up
      if (foliagePending()) { requestAnimationFrame(tick); return; }
      refreshFoliageTextures();
      this.viewport.invalidate();
    };
    requestAnimationFrame(tick);
  }

  /** Replace the planting. `ground` is the shaped terrain triangles; empty clears. */
  private plant(ground: Float32Array[]): void {
    if (this.vegetation) {
      this.viewport.scene.remove(this.vegetation);
      disposeVegetation(this.vegetation);
      this.vegetation = null;
    }
    const density = this.terrainDisplay.vegetation;
    if (!ground.length || density <= 0) {
      this.applyTheme();   // sky off — the planting just went away
      return;
    }
    try {
      const planted = buildVegetation(ground, { density });
      if (planted) {
        this.vegetation = planted;
        this.viewport.scene.add(planted);
        if (this.ghost) this.hideOverlays();   // a component FE Mesh view is up
        this.redrawWhenFoliageArrives();
      }
      this.applyTheme();   // sky on
    } catch (e) {
      // Scenery is never worth losing the model over.
      console.warn('[BridgeViewport] vegetation failed', e);
    }
  }

  /**
   * Change the terrain options for a bridge payload and re-shape what is already
   * on screen. The camera is left alone — these are used while looking at
   * something. Returns "" or an error message.
   */
  setTerrainDisplay(optionsJson: string): string {
    this.terrainDisplay = parseTerrainOptions(optionsJson, this.terrainDisplay);
    if (!this.lastPayload) return '';
    try {
      this.applyBridgeGeometry(this.lastPayload);
      this.viewport.invalidate();
      return '';
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error('[BridgeViewport] terrain redisplay failed', e);
      return message;
    }
  }

  /**
   * Read a LandXML file, draw its terrain and alignments in this viewport, and
   * return what the host needs to list the alignments — see io/LandXmlScene.ts.
   *
   * Parsing happens here rather than in C# because the payload it produces is a
   * viewer payload: recentred, scaled to metres and turned Y-up. The alignment
   * that eventually gets IMPORTED is re-read on the .NET side by
   * xApp.ReadLandXMLFile, so nothing structural depends on this parse — it draws
   * the map the choice is made on.
   *
   * Returns a JSON `LandXmlSummary`, or `{"error": "..."}` if the file could not
   * be read. Never throws across the interop boundary: a JS exception surfaces in
   * Blazor as an opaque JSException with the stack lost.
   */
  loadLandXml(xml: string, fileName?: string, optionsJson?: string): string {
    try {
      this.mapDisplay = parseDisplayOptions(optionsJson, DEFAULT_DISPLAY);
      const scene = buildLandXmlScene(xml, fileName, this.mapDisplay);
      this.mapDoc = scene.doc;
      this.bridge.setGeometry(scene.payload);
      this.applyElevationLegend(scene.summary.elevation);
      // Grid first: it is wider than the model (snapped out to whole cells, plus
      // the labels outside that), and both the fog and the framing below are
      // sized from the bounds it widens.
      this.buildSurveyGrid(scene.frame, scene.groundY);
      this.fitEnvironmentToContent();
      if (this.clip.isVisible) this.clip.setBounds(this.currentBounds() ?? DEFAULT_BOUNDS);
      this.viewport.invalidate();

      // Plan, not the usual isometric. A map is read in plan - it is how the
      // alignment this screen exists to choose is recognised - and isometric is
      // also the worse fit here for a mechanical reason: FrameExtents pushes the
      // camera back by the box's half-DEPTH, which is metres on a bridge and
      // hundreds of metres on a square kilometre of terrain, so an isometric
      // survey lands at about a fifth of the frame. Measured on the samples:
      // plan fills 51-83% of the canvas, isometric 10-18%. Isometric is still one
      // click away on the toolbar.
      this.setView('top', false);

      return JSON.stringify(scene.summary);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error('[BridgeViewport] LandXML import failed', e);
      return JSON.stringify({ error: message });
    }
  }

  /**
   * Re-draw the current map with different display options (Wireframe /
   * Subdivide), from the parsed document rather than the file.
   *
   * The camera is deliberately left where it is: these controls are used while
   * looking at something, and reframing on every click would throw the view away.
   * The graticule is left alone too — `preserveEdges` holds the surface outline,
   * so the extent it is drawn against has not moved.
   *
   * Returns the refreshed summary (its warnings carry anything the face budget
   * had to refuse), or `{"error": …}`.
   */
  setMapDisplay(optionsJson: string): string {
    if (!this.mapDoc) return '{"error":"No map is loaded."}';
    try {
      this.mapDisplay = parseDisplayOptions(optionsJson, this.mapDisplay);
      const scene = buildSceneFromDoc(this.mapDoc, this.mapDisplay);
      this.bridge.setGeometry(scene.payload);
      this.applyElevationLegend(scene.summary.elevation);
      this.viewport.invalidate();
      return JSON.stringify(scene.summary);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error('[BridgeViewport] LandXML redisplay failed', e);
      return JSON.stringify({ error: message });
    }
  }

  /**
   * Relax the atmosphere for a model far larger than a bridge.
   *
   * The viewport's defaults are sized for structures: FogExp2 at 0.0006 is 98%
   * clear at 200 m, and the camera and dolly clamps stop at 5 km. A LandXML
   * survey is 2-4 km across, which puts ALL of it deep in the fog - at 3 km the
   * fog is 97% opaque, and in light mode the fog colour is white, so a perfectly
   * good terrain mesh renders as an empty white canvas. That is not a subtle
   * washing-out; it is the whole map gone.
   *
   * So the fog is stretched to the model instead of the model being lost in it.
   * Both adjustments only ever RELAX the defaults - `Math.min` on density,
   * `Math.max` on the distances - so anything of ordinary bridge size keeps
   * exactly the look it has today. The crossover is around 420 m.
   */
  private fitEnvironmentToContent(): void {
    const box = this.currentBounds();
    if (!box || box.isEmpty()) return;

    const size = box.getSize(new THREE.Vector3());
    const diag = Math.max(1, size.length());

    // 0.25 / diagonal leaves the far side of the model about 20% hazed: enough
    // to read as depth, nowhere near enough to hide it.
    const fog = this.viewport.scene.fog as THREE.FogExp2 | null;
    if (fog && typeof fog.density === 'number') {
      fog.density = Math.min(DEFAULT_FOG_DENSITY, 0.25 / diag);
    }

    // Framing a model of this size puts the camera roughly a diagonal away, and
    // its far corners a diagonal beyond that; 5 km would clip a big survey and
    // stop the wheel dead halfway out.
    const reach = diag * 3;
    const cam = this.viewport.camera;
    if (cam.far < reach) {
      cam.far = reach;
      cam.updateProjectionMatrix();
    }
    this.viewport.controls.maxDistance = Math.max(this.viewport.controls.maxDistance, reach);
  }

  /**
   * Draw the survey graticule under the map: minor and major lines on the ground
   * plane with the site's own easting/northing on every major line, all four edges.
   *
   * Depth testing is OFF, like the label layer's. In plan - which is how this
   * screen opens, and how a grid is read - a graticule that respects depth is
   * simply invisible wherever terrain covers it, which is everywhere that matters.
   * Drawn over, at low contrast, it behaves like the grid printed over a survey
   * drawing. Ordering keeps it under the labels and over nothing else.
   */
  private buildSurveyGrid(frame: SurveyFrame, groundY: number): void {
    this.clearMapGrid();

    const box = this.currentBounds();
    if (!box || box.isEmpty()) return;

    const grid = buildMapGrid(
      { minX: box.min.x, maxX: box.max.x, minZ: box.min.z, maxZ: box.max.z },
      groundY, frame,
    );
    if (!grid) return;

    const dark = prefersDark();
    const group = new THREE.Group();
    group.name = 'MapGrid';

    const addLines = (pts: Float32Array, role: 'minor' | 'major', opacity: number, order: number) => {
      if (pts.length < 6) return;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
      const mat = new THREE.LineBasicMaterial({
        color: MAP_GRID_COLORS[role][dark ? 1 : 0],
        transparent: true, opacity, depthTest: false, depthWrite: false,
      });
      const seg = new THREE.LineSegments(geo, mat);
      seg.renderOrder = order;
      seg.userData.gridRole = role;
      group.add(seg);
    };

    addLines(grid.minor, 'minor', 0.5, 1);
    addLines(grid.major, 'major', 0.9, 2);

    // White vertex colours so applyMapGridTheme can tint the whole mesh.
    const text = buildTextMesh(grid.labels.map((l) => ({
      text: l.text, x: l.x, y: groundY, z: l.z, dx: 1, dz: 0,
      fill: '#ffffff', size: grid.labelSize,
    })));
    if (text) {
      (text.material as THREE.MeshBasicMaterial).color.setHex(MAP_GRID_COLORS.label[dark ? 1 : 0]);
      text.renderOrder = 3;
      text.userData.gridRole = 'label';
      group.add(text);
    }

    this.mapGrid = group;
    this.mapGridBox = new THREE.Box3().setFromObject(group);
    this.viewport.scene.add(group);
    if (this.ghost) this.hideOverlays();   // a component FE Mesh view is up
  }

  private clearMapGrid(): void {
    if (!this.mapGrid) return;
    this.viewport.scene.remove(this.mapGrid);
    disposeLabels(this.mapGrid);   // disposes geometry + material of every child
    this.mapGrid = null;
    this.mapGridBox = null;
    this.mapDoc = null;
    this.plant([]);
    this.elevationRange = undefined;
    this.showLegend();
  }

  /** The map's elevation range, while its legend is wanted (applyElevationLegend). */
  private elevationRange: ElevationRange | undefined;
  /** True while the FE results legend (setColorScale) is up. The widget is shared, and
   *  the results one wins: a bridge reload during Results re-applies the elevation
   *  legend — to nothing, without terrain — and used to hide the force scale with it. */
  private resultsLegend = false;

  /** Show whichever legend is wanted: the results one, else the elevation one, else none. */
  private showLegend(): void {
    if (this.resultsLegend) return;
    const range = this.elevationRange;
    if (!range) {
      this.colorScale.setStops();
      this.colorScale.clear();
      return;
    }
    this.colorScale.setStops(rampFor(this.terrainDisplay.vegetation > 0));
    this.colorScale.setScale(range.min, range.max, `Elevation (${unitAbbrev(range.unit)})`);
  }

  /**
   * Show (or hide) the elevation legend for an imported map.
   *
   * The same widget the FE contour uses, restopped to the terrain ramp — the bar
   * and the shading are driven from one array (ELEVATION_STOPS), so the colour a
   * reader matches against the legend is the colour they are looking at. The FE
   * palette is restored on the way out, because the instance is shared and
   * whatever shows a scale next has every right to its own.
   */
  private applyElevationLegend(range: ElevationRange | undefined): void {
    this.elevationRange = range;
    this.showLegend();
  }

  /** What the last load() had to do differently, or "" — see BridgeScene.lastNotice. */
  loadNotice(): string { return this.bridge.lastNotice ?? ''; }

  zoomAll(): void { this.frame(true, true); }
  zoomIn(): void { this.viewport.ZoomIn(); }
  zoomOut(): void { this.viewport.ZoomOut(); }
  zoomWindow(): void { this.viewport.zoomWindow(); }
  setProjection(ortho: boolean): void {
    this.viewport.setProjection(!!ortho);
    this.syncRotationLock();
  }

  /** Settings > 2D Display > Ortho Zoom Speed, as a multiplier on the default
   *  wheel step. Only the orthographic camera's zoom is affected — the
   *  perspective dolly keeps its own (slower) speed. */
  setOrthoZoomSpeed(speed: number): void { this.viewport.setOrthoZoomSpeed(Number(speed)); }

  /** Rotation is locked whenever an axis-aligned Ortho standard view is active —
   *  Top/Bottom/Front/Back/Left/Right stop meaning what their name says the
   *  moment you orbit away from them. Isometric is exempt (it's a free-look
   *  view by nature), and Perspective is never locked. */
  private syncRotationLock(): void {
    this.viewport.setRotationLocked(this.viewport.orthographic && this.currentView !== 'iso');
  }

  setRotateMode(mode: string): void {
    if (mode === 'point') { this.viewport.setOrbitPointMode(true); return; }
    this.viewport.setOrbitPointMode(false);
    const b = this.currentBounds();
    if (b && !b.isEmpty()) {
      const c = b.getCenter(new THREE.Vector3());
      this.viewport.controls.setOrbitPoint(c.x, c.y, c.z);
    }
  }

  /**
   * Fit the current content on screen. `reset` means "reset the view AND zoom
   * all": re-home the camera to the canonical isometric angle rather than
   * tightening whatever angle is on screen. That's what the toolbar's Zoom All
   * button asks for — one click gets you back to the home view, from a
   * standard view or a free orbit alike. The geometry-load paths pass false so
   * a refresh doesn't yank a deliberately picked Top/Front view to isometric;
   * they still re-home when Isometric is the active view, since — unlike the
   * axis-locked standard views (see syncRotationLock) — it's exempt from the
   * rotation lock, so the camera can have drifted off the canonical angle
   * while `currentView` still reads 'iso'.
   */
  private frame(animate: boolean, reset = false): void {
    const box = this.currentBounds();
    if (!box || box.isEmpty()) return;

    if (reset || this.currentView === 'iso') { this.setView('iso', animate); return; }

    // A standard view (locked, so it can't drift) being refit after a load:
    // tighten the frame in place, don't reset it.
    this.viewport.ZoomAllPreserveDirection(box, 1.2, animate);
  }

  private currentBounds(): THREE.Box3 | null {
    if (this.feActive && this.fe.hasContent) {
      // A component's solid is wider than its mesh (a column's frame is its centre line).
      const box = this.fe.getBounds();
      if (this.ghost) box.union(new THREE.Box3().setFromObject(this.ghost));
      const ov = this.fe.getOverlayBounds();
      if (ov && !ov.isEmpty()) box.union(ov);
      return box;
    }
    if (!this.bridge.hasContent) return null;
    const box = this.bridge.getBounds();
    // The survey graticule is deliberately bigger than what it measures, and its
    // edge labels are the outermost thing on screen. Leaving it out of the bounds
    // is what makes Zoom All cut the numbers off.
    if (this.mapGridBox) box.union(this.mapGridBox);
    return box;
  }

  /** The see-through solid of the component whose FE mesh is shown (see
   *  `loadFEMesh`'s ghostIds); null for the whole-model mesh. */
  private ghost: THREE.Group | null = null;

  /** Show an FE mesh in place of the bridge. `ghostIds` is the Explorer's
   *  per-component FE Mesh view: the payload is one component's elements, and
   *  the component's own solid — those bridge parts, see-through — is drawn
   *  round them, so the mesh reads against the thing it was cut from. The rest
   *  of the bridge stays hidden. The camera frames what is shown. */
  loadFEMesh(json: string, keepView: boolean = false, ghostIds?: string[] | null): void {
    let payload: FEPayload;
    try {
      payload = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse FE mesh payload', e);
      return;
    }
    this.fe.setMesh(payload);
    this.feActive = true;
    this.meshGizmo?.detach();
    this.fe.root.visible = true;
    this.fe.syncOverlayVisibility();
    this.bridge.root.visible = false;
    this.clearGhost();
    this.ghostIds = ghostIds && ghostIds.length ? ghostIds : null;
    this.buildGhosts();
    this.setDiagramLabels(payload as FEPayload & { diagramLabelPos?: number[]; diagramLabelText?: string[] });
    if (this.clip.isVisible) this.clip.setBounds(this.currentBounds() ?? DEFAULT_BOUNDS);
    this.viewport.invalidate();
    // Animation playback passes keepView so each frame swap doesn't re-frame the
    // camera out from under the user — they should be free to rotate/pan while it plays.
    if (!keepView) this.frame(false);
  }

  /** Toggle the bottom-docked section-clipping panel (X/Y/Z crop-box
   *  sliders). Turning it on re-frames the sliders on the current model's
   *  bounds and installs the crop planes; turning it off removes the crop
   *  entirely (the sliders' positions are kept in memory for next time). */
  setClipping(on: boolean): void {
    if (on) this.clip.show(this.currentBounds() ?? DEFAULT_BOUNDS);
    else this.clip.hide();
    // The Results contour legend shares the bottom-center dock — stack it
    // above the clipping card instead of letting the two overlap.
    this.colorScale.setBottomOffset(this.clip.outerHeight);
  }

  /** Register the Blazor toolbar's DotNetObjectReference so the panel's own
   *  close button can call back into it (OnClippingClosed) and un-highlight
   *  the toggle button, instead of only the toolbar being able to close it. */
  registerClipCloseCallback(dotNetRef: PickRef): void {
    this.clipCloseRef = dotNetRef;
  }

  /** Element Results "Play" setup, part 1 of 2 (see FEAnimator.ts's header):
   *  the one-time mesh snapshot .NET sends before either play* call. */
  loadFEAnimationMesh(json: string): void {
    this.feAnim.loadMesh(json);
  }

  /** Play setup, part 2: ramp/oscillate ONE displacement field's scale —
   *  +1..-1 for Modal, 0..+1 for everything else — computing the whole ramp up
   *  front (cheap: it's just numJoints × ~11-21 steps of arithmetic) so the
   *  playback loop itself is a plain array index, no per-tick work beyond one
   *  FEAnimator.applyFrame() call. */
  playFEScaleAnimation(fieldJson: string, modal: boolean, baseScale: number, intervalMs: number): void {
    this.stopFEAnimation();
    if (!this.feAnim.ready) return;
    let field: DisplacementField;
    try {
      field = typeof fieldJson === 'string' ? JSON.parse(fieldJson) : fieldJson;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse FE displacement field', e);
      return;
    }
    const lo = modal ? -1.0 : 0.0;
    const frames: Array<{ field: DisplacementField; scale: number }> = [];
    for (let f = 1.0; f >= lo - 1e-9; f -= 0.1) frames.push({ field, scale: baseScale * f });
    this.startFEAnimation(frames, intervalMs);
  }

  /** Play setup, part 2 (step-cycling variant): one displacement field per
   *  genuine step (moving load / multi-step nonlinear / staged construction),
   *  scale held fixed at the user's chosen display scale. */
  playFEStepAnimation(fieldsJson: string, scale: number, intervalMs: number): void {
    this.stopFEAnimation();
    if (!this.feAnim.ready) return;
    let fields: DisplacementField[];
    try {
      fields = typeof fieldsJson === 'string' ? JSON.parse(fieldsJson) : fieldsJson;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse FE displacement fields', e);
      return;
    }
    if (!Array.isArray(fields) || !fields.length) return;
    this.startFEAnimation(fields.map((field) => ({ field, scale })), intervalMs);
  }

  private startFEAnimation(frames: Array<{ field: DisplacementField; scale: number }>, intervalMs: number): void {
    if (!frames.length) return;
    this.animFrames = frames;
    this.animIndex = 0;
    this.animDir = 1;
    this.animIntervalMs = Math.max(16, intervalMs);
    this.animLastTick = performance.now();
    this.feAnim.applyFrame(frames[0].field, frames[0].scale);
    this.viewport.invalidate();
    this.animRafId = requestAnimationFrame(this.animStep);
  }

  stopFEAnimation(): void {
    if (this.animRafId != null) { cancelAnimationFrame(this.animRafId); this.animRafId = null; }
    this.animFrames = [];
  }

  clearFEMesh(): void {
    this.stopFEAnimation();
    this.feActive = false;
    this.fe.clear();
    this.fe.root.visible = false;
    this.fe.clearOverlay();
    this.clearGhost();
    this.disposeModelGhost();
    this.ghostIds = null;
    this.clearDiagramLabels();
    this.bridge.root.visible = true;
    this.viewport.invalidate();
    this.frame(false);
  }

  /** What the bridge view draws round the bridge — layout line, station and grid
   *  labels, datum, axes, ground grid, map, planting. A component's FE Mesh view is
   *  that component alone: these go while it is up, and come back as they were. */
  private overlayVisibility: Map<THREE.Object3D, boolean> | null = null;

  private overlays(): THREE.Object3D[] {
    return [this.grid, this.mapGrid, this.vegetation, this.axes, this.labelsGroup, this.datumGroup]
      .filter((o): o is THREE.Object3D => o != null);
  }

  private hideOverlays(): void {
    if (!this.overlayVisibility) this.overlayVisibility = new Map();
    for (const o of this.overlays()) {
      if (!this.overlayVisibility.has(o)) this.overlayVisibility.set(o, o.visible);
      o.visible = false;
    }
  }

  private restoreOverlays(): void {
    if (!this.overlayVisibility) return;
    for (const [o, visible] of this.overlayVisibility) o.visible = visible;
    this.overlayVisibility = null;
  }

  /** The values written at the frame diagram's tips (FE payload `diagramLabelPos` /
   *  `diagramLabelText`), as camera-facing text of a fixed screen size — a diagram is
   *  read from every side, and flat plan lettering disappears edge-on. */
  private diagramLabels: THREE.Group | null = null;

  private setDiagramLabels(payload: { diagramLabelPos?: number[]; diagramLabelText?: string[] }): void {
    this.clearDiagramLabels();
    const pos = payload.diagramLabelPos ?? [];
    const text = payload.diagramLabelText ?? [];
    if (!text.length || pos.length < text.length * 3) return;

    const g = new THREE.Group();
    g.name = 'FEDiagramLabels';
    for (let i = 0; i < text.length; i++) {
      const sprite = makeTextSprite(text[i]);
      sprite.userData.bw = sprite.scale.x;
      sprite.userData.bh = sprite.scale.y;
      sprite.scale.multiplyScalar(this.diagramLabelScale);
      sprite.position.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);
      g.add(sprite);
    }
    this.diagramLabels = g;
    this.viewport.scene.add(g);

    // sizeAttenuation keeps a sprite's screen size only under a perspective camera; an
    // orthographic one reads the scale as world units. Restrike it each frame there.
    if (!this.diagramLabelsOnFrame) {
      this.diagramLabelsOnFrame = true;
      this.viewport.OnFrame(() => {
        const labels = this.diagramLabels;
        if (!labels) return;
        const cam = this.viewport.camera;
        const k = (cam as THREE.OrthographicCamera).isOrthographicCamera
          ? ((cam as THREE.OrthographicCamera).top - (cam as THREE.OrthographicCamera).bottom) / (cam as THREE.OrthographicCamera).zoom / 0.83
          : 1;
        for (const o of labels.children) {
          const s = o as THREE.Sprite & { userData: { w?: number; h?: number } };
          if (s.userData.w == null) { s.userData.w = s.scale.x; s.userData.h = s.scale.y; }
          s.scale.set(s.userData.w * k, s.userData.h! * k, 1);
        }
        this.declutterDiagramLabels(cam);
      });
    }
  }

  /** 3D toolbar ▸ Settings ▸ Diagram label scale: the labels' size relative to makeTextSprite's. */
  private diagramLabelScale = DIAGRAM_LABEL_SCALE;

  setDiagramLabelScale(k: number): void {
    if (!(k > 0)) return;
    this.diagramLabelScale = k;
    const labels = this.diagramLabels;
    if (labels) {
      // The per-frame restrike works from userData.w/h — reset it to the new base size.
      for (const o of labels.children) {
        const s = o as THREE.Sprite;
        s.userData.w = s.userData.bw * k;
        s.userData.h = s.userData.bh * k;
        s.scale.set(s.userData.w, s.userData.h, 1);
      }
      this.diagramLabelsCount = -1;   // re-run the declutter: smaller labels clear more room
    }
    this.viewport.invalidate();
  }

  private diagramLabelsOnFrame = false;
  private diagramLabelsView = new THREE.Matrix4();
  private diagramLabelsCount = -1;

  /** Only the labels that matter, and none on top of another: the payload lists them most
   *  important first (each way's peak, then the rest largest first), and each is shown only
   *  where its box, padded half its own size, clears every box already shown. Rerun when the
   *  camera moves — what overlaps depends on where the diagram is seen from. */
  private declutterDiagramLabels(cam: THREE.Camera): void {
    const labels = this.diagramLabels;
    if (!labels) return;
    cam.updateMatrixWorld();
    const pc = cam as THREE.PerspectiveCamera;
    const view = new THREE.Matrix4().multiplyMatrices(pc.projectionMatrix, cam.matrixWorldInverse);
    if (labels.children.length === this.diagramLabelsCount && view.equals(this.diagramLabelsView)) return;
    this.diagramLabelsView.copy(view);
    this.diagramLabelsCount = labels.children.length;

    // A sprite's size in normalised device units is its scale times the projection's
    // diagonal — the same for both cameras once the orthographic restrike above is applied.
    const px = pc.projectionMatrix.elements[0], py = pc.projectionMatrix.elements[5];
    const kept: { l: number; r: number; b: number; t: number }[] = [];
    const p = new THREE.Vector3();
    for (const o of labels.children) {
      p.copy(o.position).applyMatrix4(view);
      if (p.z < -1 || p.z > 1 || Math.abs(p.x) > 1.2 || Math.abs(p.y) > 1.2) { o.visible = false; continue; }
      const hw = o.scale.x * px * 0.5 * 1.5, hh = o.scale.y * py * 0.5 * 1.5;
      const box = { l: p.x - hw, r: p.x + hw, b: p.y - hh, t: p.y + hh };
      const clear = !kept.some((k) => box.l < k.r && k.l < box.r && box.b < k.t && k.b < box.t);
      o.visible = clear;
      if (clear) kept.push(box);
    }
  }

  private clearDiagramLabels(): void {
    if (!this.diagramLabels) return;
    this.viewport.scene.remove(this.diagramLabels);
    this.diagramLabels.traverse((o) => {
      const s = o as THREE.Sprite;
      if (!(s as unknown as { isSprite?: boolean }).isSprite) return;
      s.material.map?.dispose();
      s.material.dispose();
    });
    this.diagramLabels = null;
    this.diagramLabelsCount = -1;
  }

  /** The component the FE Mesh view is showing (loadFEMesh's ghostIds), kept so the
   *  model overlay can be switched without the host sending the mesh again. */
  private ghostIds: string[] | null = null;

  /** 3D toolbar ▸ Model overlay: the bridge drawn see-through gray round the FE mesh —
   *  round a component's, the rest of the bridge gray and the component itself
   *  see-through yellow (off, the component is drawn alone in gray); round the whole
   *  analysis model, all of it gray. Held across loads. */
  private feModelOverlay = false;
  /** The overlay's surface opacity, 0–1 (3D toolbar ▸ Model overlay's transparency). */
  private feModelOverlayOpacity = 0.14;
  private modelGhost: THREE.Group | null = null;
  /** Which component `modelGhost` leaves out — the same component's next load (a new
   *  case or force) keeps it rather than copying the whole bridge again. */
  private modelGhostKey: string | null = null;

  private buildGhosts(): void {
    // No component: the whole analysis model (Results ▸ Analysis Model), so the overlay
    // is the whole bridge, and nothing is picked out in yellow.
    const ids = this.ghostIds ?? [];

    if (ids.length) {
      this.ghost = this.feModelOverlay
        ? this.bridge.buildGhost(ids, { color: 0xffd21f, opacity: 0.38, edgeColor: 0x9a7a00, edgeOpacity: 0.7 })
        : this.bridge.buildGhost(ids);
      if (this.ghost) this.viewport.scene.add(this.ghost);
    }

    const key = ids.join('|');
    if (!this.feModelOverlay) this.disposeModelGhost();
    else if (!this.modelGhost || this.modelGhostKey !== key) {
      this.disposeModelGhost();
      const o = this.feModelOverlayOpacity;
      this.modelGhost = this.bridge.buildGhost(ids, { color: 0xa9adb3, opacity: o, edgeOpacity: overlayEdgeOpacity(o), exclude: true });
      this.modelGhostKey = key;
      if (this.modelGhost) this.viewport.scene.add(this.modelGhost);
    }
    // A component is shown alone, so what is drawn round the bridge goes; the whole
    // model keeps its stations and grid, overlay or not.
    if (ids.length && (this.ghost || this.modelGhost)) this.hideOverlays();
  }

  setFEModelOverlay(on: boolean): void {
    this.feModelOverlay = !!on;
    if (!this.feActive) return;
    if (this.ghost) { this.viewport.scene.remove(this.ghost); disposeGroup(this.ghost); this.ghost = null; }
    this.buildGhosts();
    this.viewport.invalidate();
  }

  /** The model overlay's opacity (0–1), applied to the one already drawn in place —
   *  the slider drags it, and rebuilding a whole bridge per tick would not keep up. */
  setFEModelOverlayOpacity(opacity: number): void {
    const o = Math.max(0.02, Math.min(1, Number(opacity) || 0));
    this.feModelOverlayOpacity = o;
    if (!this.modelGhost) return;
    this.modelGhost.traverse((obj) => {
      const m = (obj as THREE.Mesh).material as THREE.Material | undefined;
      if (!m) return;
      m.opacity = obj.name === 'FEGhostEdges' ? overlayEdgeOpacity(o) : o;
    });
    this.viewport.invalidate();
  }

  /** Centre of the parts in `ids`, viewer space (m, Y-up), or null. */
  partsCenter(ids: string[]): number[] | null { return this.bridge.partsCenter(ids); }

  private disposeModelGhost(): void {
    if (!this.modelGhost) return;
    this.viewport.scene.remove(this.modelGhost);
    disposeGroup(this.modelGhost);
    this.modelGhost = null;
    this.modelGhostKey = null;
  }

  /** The component's ghost only: the model overlay outlives a reload of the same
   *  component (see modelGhostKey) and goes in buildGhosts or clearFEMesh. */
  private clearGhost(): void {
    this.restoreOverlays();
    if (this.ghost) {
      this.viewport.scene.remove(this.ghost);
      disposeGroup(this.ghost);
      this.ghost = null;
    }
  }

  setFEVisible(type: string, on: boolean): void { this.fe.setVisible(type, on); this.viewport.invalidate(); }

  setFEDepthTest(disabled: boolean): void { this.fe.setDepthTest(disabled); this.viewport.invalidate(); }

  setFEExtrude(on: boolean): void { this.fe.setExtrude(on); this.viewport.invalidate(); }
  setFEShellOpacity(opacity: number): void { this.fe.setShellOpacity(Number(opacity) || 0); this.viewport.invalidate(); }
  /** 3D toolbar ▸ Settings ▸ Line antialiasing: 'msaa' | 'supersample' (Viewport3D.setAntialiasMode). */
  setAntialiasMode(mode: string): void {
    this.viewport.setAntialiasMode(mode === 'supersample' ? 'supersample' : 'msaa');
    this.viewport.invalidate();
  }

  setColorScale(min: number, max: number, title: string): void {
    this.resultsLegend = true;
    this.colorScale.setStops();   // the FE scale, not a terrain ramp left from before
    this.colorScale.setScale(min, max, title);
    this.colorScale.setBottomOffset(this.clip.outerHeight);
  }

  clearColorScale(): void {
    this.resultsLegend = false;
    this.showLegend();
  }

  setReactionArrows(json: string): void {
    let payload: ReactionArrowsPayload;
    try {
      payload = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse reaction-arrows payload', e);
      return;
    }
    this.reactionArrows.set(payload);
    this.viewport.invalidate();
  }

  clearReactionArrows(): void { this.reactionArrows.clear(); this.viewport.invalidate(); }

  highlightFE(json: string): void {
    if (!json || (typeof json === 'string' && json.trim() === '')) { this.fe.clearHighlight(); this.viewport.invalidate(); return; }
    let payload: FEPayload;
    try {
      payload = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse FE highlight payload', e);
      return;
    }
    this.fe.setHighlight(payload);
    this.viewport.invalidate();
  }

  clearFEHighlight(): void { this.fe.clearHighlight(); this.viewport.invalidate(); }

  /** 3D toolbar ▸ Express mesh: a second FE mesh drawn over the one on screen, in one
   *  colour (see FEScene.setOverlay). Held across loadFEMesh; gone with clearFEMesh. */
  setFEOverlay(json: string): void {
    let payload: FEPayload;
    try {
      payload = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse FE overlay payload', e);
      return;
    }
    this.fe.setOverlay(payload);
    this.viewport.invalidate();
  }

  clearFEOverlay(): void { this.fe.clearOverlay(); this.viewport.invalidate(); }

  enableFePicking(dotNetRef: PickRef): void {
    this.fePickRef = dotNetRef;
    if (this.fePickWired) return;
    this.fePickWired = true;
    const el = this.viewport.container;
    el.addEventListener('pointerdown', (e: PointerEvent) => {
      this.downX = e.clientX; this.downY = e.clientY; this.downT = performance.now();
    });
    el.addEventListener('pointerup', (e: PointerEvent) => {
      if (e.button !== 0) return;
      const moved = Math.hypot(e.clientX - this.downX, e.clientY - this.downY);
      if (moved > 5 || performance.now() - this.downT > 500) return;
      this.pickFEElement(e);
    });
  }

  private pickFEElement(e: PointerEvent): void {
    if (!this.feActive || !this.fePickRef) return;
    // NOTE: no orbit-point guard here. Point-rotate is now the default mode, so
    // suppressing picks while it is armed would mean the FE mesh could never be
    // clicked. The two do not collide: the pivot is set on pointerdown, the pick
    // resolves on a pointerup that did not drag.

    const rect = this.viewport.container.getBoundingClientRect();
    this.feNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.feNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.fePicker.setFromCamera(this.feNdc, this.viewport.camera);
    this.fePicker.params.Line = { threshold: Math.max(0.05, this.viewport.controls.distance * 0.01) };

    // Only raycast against whichever representation is actually visible (thin
    // lines/flat surface, or the extruded solid when "Extrude" is on) — an
    // invisible object still gets hit-tested by three.js's raycaster otherwise.
    const targets: THREE.Object3D[] = [];
    if (this.fe.shellMesh?.visible) targets.push(this.fe.shellMesh);
    if (this.fe.shellExtrudeMesh?.visible) targets.push(this.fe.shellExtrudeMesh);
    if (this.fe.frameLines?.visible) targets.push(this.fe.frameLines);
    if (this.fe.frameMesh?.visible) targets.push(this.fe.frameMesh);
    if (this.fe.diagramMesh?.visible) targets.push(this.fe.diagramMesh);
    if (!targets.length) return;

    const hits = this.fePicker.intersectObjects(targets, false);
    if (!hits.length) return;
    const h = hits[0];

    if (h.object === this.fe.shellMesh && h.faceIndex != null) {
      const idx = this.fe.shellTriElem[h.faceIndex];
      if (idx != null) void this.fePickRef.invokeMethodAsync('OnFeElementPicked', 'shell', idx);
    } else if (h.object === this.fe.shellExtrudeMesh && h.faceIndex != null) {
      const idx = this.fe.shellExtrudeTriElem[h.faceIndex];
      if (idx != null) void this.fePickRef.invokeMethodAsync('OnFeElementPicked', 'shell', idx);
    } else if (h.object === this.fe.frameLines && h.index != null) {
      void this.fePickRef.invokeMethodAsync('OnFeElementPicked', 'frame', h.index >> 1);
    } else if (h.object === this.fe.frameMesh && h.faceIndex != null) {
      const idx = this.fe.frameTriElem[h.faceIndex];
      if (idx != null) void this.fePickRef.invokeMethodAsync('OnFeElementPicked', 'frame', idx);
    } else if (h.object === this.fe.diagramMesh && h.faceIndex != null) {
      const idx = this.fe.diagramTriElem[h.faceIndex];
      if (idx != null) void this.fePickRef.invokeMethodAsync('OnFeElementPicked', 'frame', idx);
    }
  }

  enableBridgePicking(dotNetRef: PickRef): void {
    this.bridgePickRef = dotNetRef;
    if (this.bridgePickWired) return;
    this.bridgePickWired = true;
    const el = this.viewport.container;
    el.addEventListener('pointerdown', (e: PointerEvent) => {
      this.bDownX = e.clientX; this.bDownY = e.clientY; this.bDownT = performance.now();
    });
    el.addEventListener('pointerup', (e: PointerEvent) => {
      if (e.button !== 0 || !this.bridgeSelectMode || this.feActive) return;
      // A click on a gizmo handle is the gizmo's, not a pick of whatever is behind it.
      if (this.meshGizmo?.consumedPointer) return;
      // Point-rotate is the default mode; it re-pivots on pointerdown and leaves
      // this click-to-select on pointerup alone (see pickFEElement).
      const moved = Math.hypot(e.clientX - this.bDownX, e.clientY - this.bDownY);
      if (moved > 5 || performance.now() - this.bDownT > 500) return;
      this.pickBridgeComponent(e);
    });
  }

  setSelectMode(on: boolean): void {
    this.bridgeSelectMode = !!on;
    // Leaving Select mode must not wipe the crosshair that point-rotate (the
    // default mode) put on the container.
    this.viewport.container.style.cursor = this.bridgeSelectMode
      ? 'pointer'
      : this.viewport.orbitPointActive ? 'crosshair' : '';
  }

  private pickBridgeComponent(e: PointerEvent): void {
    if (!this.bridgePickRef) return;
    const rect = this.viewport.container.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    // A marker in front of the surface is what was aimed at.
    if (this.markerGroup && this.markerPickRef) {
      this.fePicker.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.viewport.camera);
      const m = this.fePicker.intersectObjects(this.markerGroup.children, false)[0];
      const markerId = m ? this.markerIds.get(m.object.uuid) : undefined;
      if (markerId) {
        void this.markerPickRef.invokeMethodAsync('OnMarkerPicked', markerId);
        return;
      }
    }
    this.lastPickHit = this.bridge.pickHit(ndcX, ndcY, this.viewport.camera);
    const id = this.bridge.pick(ndcX, ndcY, this.viewport.camera);
    void this.bridgePickRef.invokeMethodAsync('OnBridgePicked', id ?? '');
  }

  /** Replace the host's markers: a sphere each, lifted off the surface along its normal. */
  setMarkers(list: MarkerSpec[]): void {
    this.clearMarkers();
    if (!list.length) { this.viewport.invalidate(); return; }
    const group = new THREE.Group();
    group.name = 'host-markers';
    for (const m of list) {
      const r = m.radius ?? 0.45;
      const geom = new THREE.SphereGeometry(r, 20, 14);
      const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(m.color), transparent: (m.opacity ?? 1) < 1, opacity: m.opacity ?? 1, depthTest: true });
      const mesh = new THREE.Mesh(geom, mat);
      const n = new THREE.Vector3(...(m.normal ?? [0, 0, 0]));
      mesh.position.set(...m.position).addScaledVector(n.lengthSq() > 0 ? n.normalize() : n, r * 0.8);
      mesh.renderOrder = 10;
      group.add(mesh);
      this.markerIds.set(mesh.uuid, m.id);
      if (m.label?.length) {
        // Anchored under its bottom edge, so the card stands clear above the pin; a click on it is a click on the pin.
        const card = makeMarkerCard(m.label, m.color, m.opacity ?? 1);
        card.position.copy(mesh.position);
        card.center.set(0.5, -0.35);
        group.add(card);
        this.markerIds.set(card.uuid, m.id);
      }
    }
    this.markerGroup = group;
    this.viewport.scene.add(group);

    // The cards are sized in screen pixels (userData.hPx tall): restruck each frame from the
    // camera and the view's height — a perspective camera with sizeAttenuation off reads the
    // scale against its projection, an orthographic one as world units.
    if (!this.markerCardsOnFrame) {
      this.markerCardsOnFrame = true;
      this.viewport.OnFrame(() => {
        const g = this.markerGroup;
        if (!g) return;
        const viewH = this.viewport.container.clientHeight || 1;
        const cam = this.viewport.camera as THREE.OrthographicCamera & THREE.PerspectiveCamera;
        const perPx = cam.isOrthographicCamera
          ? (cam.top - cam.bottom) / cam.zoom / viewH
          : 2 / (cam.projectionMatrix.elements[5] * viewH);
        for (const o of g.children) {
          if (!(o as THREE.Sprite).isSprite) continue;
          const h = o.userData.hPx * perPx;
          o.scale.set(h * o.userData.aspect, h, 1);
        }
      });
    }
    this.viewport.invalidate();
  }

  private markerCardsOnFrame = false;

  private clearMarkers(): void {
    const g = this.markerGroup;
    if (!g) return;
    g.removeFromParent();
    for (const c of g.children) {
      if ((c as THREE.Sprite).isSprite) { const s = c as THREE.Sprite; s.material.map?.dispose(); s.material.dispose(); continue; }
      const mesh = c as THREE.Mesh;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.markerGroup = null;
    this.markerIds.clear();
  }

  setView(name: ViewName, animate = true): void {
    this.currentView = name;
    this.syncRotationLock();
    const box = this.currentBounds() ?? DEFAULT_BOUNDS;

    const dirs: Record<ViewName, [number, number, number]> = {
      iso: ISO_DIR,
      top: [0, 1, 0.0001],
      bottom: [0, -1, 0.0001],
      front: [0, 0.05, 1],
      back: [0, 0.05, -1],
      left: [-1, 0.05, 0],
      right: [1, 0.05, 0],
    };
    const dir = new THREE.Vector3(...(dirs[name] ?? dirs.iso));

    // FrameBoxFromDirection measures the box along this direction's own screen
    // axes, so every view — the axis-aligned ones and the diagonal isometric
    // alike — gets an exact fit. This used to be a per-view table of raw bbox
    // dimensions, which was right for Top/Front/… and only an approximation for
    // Isometric, where a diagonal footprint is wider on screen than either of
    // its sides and a tall object leans into the horizontal as well.
    this.viewport.FrameBoxFromDirection(box, dir, 1.2, animate);
  }

  setShadows(on: boolean): void { this.viewport.setShadowsEnabled(on); }

  setGrid(on: boolean): void {
    if (this.grid) { this.viewport.scene.remove(this.grid); disposeHelper(this.grid); this.grid = null; }
    if (on) {
      let size = 100, cx = 0, cz = 0;
      const b = this.bridge.getBounds();
      if (b && !b.isEmpty()) {
        size = Math.max(b.max.x - b.min.x, b.max.z - b.min.z) * 1.3;
        cx = (b.min.x + b.max.x) / 2;
        cz = (b.min.z + b.max.z) / 2;
      }
      const divisions = Math.max(10, Math.round(size / 5));
      const grid = new THREE.GridHelper(size, divisions, 0x9aa0a6, 0x6b7177);
      grid.position.set(cx, 0, cz);
      for (const m of (Array.isArray(grid.material) ? grid.material : [grid.material])) {
        m.transparent = true;
        m.opacity = 0.4;
      }
      this.grid = grid;
      this.viewport.scene.add(grid);
      if (this.ghost) this.hideOverlays();   // a component FE Mesh view is up
    }
    this.viewport.invalidate();
  }

  /**
   * Show/hide the world axis triad.
   *
   * `length` (metres, arrow length) overrides the automatic size. The automatic rule is clamped to
   * 6..18 before the /3, i.e. 2..6 m whatever the content is — fine for a bridge, but far too big
   * when a single component is previewed on its own. A caller that knows the content's true size
   * passes its own length instead.
   */
  setAxis(on: boolean, length?: number): void {
    if (this.axes) { this.viewport.scene.remove(this.axes); disposeHelper(this.axes); this.axes = null; }
    if (on) {
      let L = 12;
      const b = this.bridge.getBounds();
      if (b && !b.isEmpty())
        L = Math.min(18, Math.max(6, Math.max(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z) * 0.15));
      L /= 3;

      if (typeof length === 'number' && isFinite(length) && length > 0) L = length;

      // The triad shows MODEL axes, not scene axes: the scene is Y-up with
      // model X→x, model Z→y, model −Y→z (see BridgeScene.ts), so model Y runs
      // along scene −z and model Z is scene up.
      const parts = [
        arrowGeom(new THREE.Vector3(1, 0, 0), L, new THREE.Color(0xff4444)), // model X — red
        arrowGeom(new THREE.Vector3(0, 0, -1), L, new THREE.Color(0x44cc55)), // model Y — green
        arrowGeom(new THREE.Vector3(0, 1, 0), L, new THREE.Color(0x4488ff)), // model Z — blue
      ];
      const merged = mergeGeometries(parts, false)!;
      parts.forEach((p) => p.dispose());
      const mat = new THREE.MeshStandardMaterial({ vertexColors: true, metalness: 0.1, roughness: 0.6 });
      const mesh = new THREE.Mesh(merged, mat);
      mesh.name = 'Axis';
      this.axes = mesh;
      this.viewport.scene.add(mesh);
      if (this.ghost) this.hideOverlays();   // a component FE Mesh view is up
    }
    this.viewport.invalidate();
  }

  select(ids: string[]): void { this.selection = [...(ids ?? [])]; this.bridge.select(this.selection); this.viewport.invalidate(); }
  private selection: string[] = [];

  /**
   * Home ▸ Show Selected Only: draw only the parts in `ids`; null or empty draws
   * the whole payload again. Rebuilds from the last payload with the camera left
   * where it is, then puts the selection highlight back (setGeometry clears it).
   */
  setIsolate(ids: string[] | null): void {
    this.isolateIds = ids && ids.length ? new Set(ids) : null;
    if (!this.lastPayload) return;
    this.applyBridgeGeometry(this.lastPayload);
    this.bridge.select(this.selection);
    if (this.clip.isVisible) this.clip.setBounds(this.currentBounds() ?? DEFAULT_BOUNDS);
    this.viewport.invalidate();
  }

  /**
   * EXPERIMENT: how a selection is drawn — 'overlay' (the shipped orange
   * surface copy), 'hull' (an inverted-hull contour per component, see
   * SelectionHull.ts) or 'both', to compare them on one shot. Live, and applies
   * to the current selection immediately. Does not touch the GeometryOutline
   * edge lines, which are drawn either way.
   */
  setSelectionStyle(style: string): void {
    const s = (style ?? '').toLowerCase();
    if (s !== 'overlay' && s !== 'hull' && s !== 'both') return;
    this.bridge.setSelectionStyle(s);
    this.viewport.invalidate();
  }

  /** Contour colour ('#rrggbb') and weight (multiplier on 2.5 px). Remembered
   *  even if nothing is selected yet. */
  setHullLook(colour?: string, weight?: number): void {
    this.bridge.setHullLook(colour, weight);
    this.viewport.invalidate();
  }

  /**
   * EXPERIMENT: a contour round EVERY component rather than just the selection
   * — the "full outline" look. Independent of `setSelectionStyle`, and does not
   * touch the GeometryOutline edge lines, so all three can be seen together.
   */
  setOutlineAll(on: boolean): void {
    this.bridge.setOutlineAll(!!on);
    this.viewport.invalidate();
  }

  /**
   * Which halves of the full outline to draw, for judging the look:
   * `hull` is the per-component silhouette, `creases` the hard edges within each
   * component. A full outline is both — a hull alone leaves a girder web as a
   * flat lozenge, and creases alone lose the line between two touching
   * components. `angle` is the crease threshold in degrees (default 30; higher
   * keeps fewer edges and will start dropping chamfers).
   */
  setOutlineParts(hull: boolean, creases: boolean, angle?: number): void {
    this.bridge.setOutlineParts(!!hull, !!creases, angle);
    this.viewport.invalidate();
  }

  /**
   * EXPERIMENT: intersection lines — the curve where two solids interpenetrate,
   * which neither the contour nor the creases can draw because it is not an edge
   * of either mesh. Found in screen space from a normal discontinuity; see
   * NormalEdges.ts for why this does not cost the main image its antialiasing.
   * `angle` is the threshold in degrees (default 30), `width` a multiple of the
   * one-pixel line (default 0.5 — the hairline these ship at).
   *
   * Normally there is nothing to call: the lines follow the Geometry Outline
   * layer, which every load carries in its payload (`setGeometry`). This is for
   * overriding that from the console while judging the look.
   */
  setIntersectionLines(on: boolean, angle?: number, width?: number): void {
    this.bridge.setIntersectionLines(!!on, angle, width);
    this.viewport.invalidate();
  }
  setTransparency(value: number): void {
    const v = Math.max(1, Math.min(100, value));
    this.bridge.setOpacity(v / 100);
    this.viewport.invalidate();
  }

  setLabels(json: string): void {
    if (this.labelsGroup) { this.viewport.scene.remove(this.labelsGroup); disposeLabels(this.labelsGroup); this.labelsGroup = null; }

    let payload: { labels?: LabelDef[]; lines?: number[]; north?: [number, number] } = {};
    try { payload = JSON.parse(json); } catch { payload = {}; }

    const g = new THREE.Group();
    g.name = 'BridgeLabels';

    const texts: TextItem[] = [];
    for (const d of (payload.labels ?? [])) {
      if (!d || !d.text) continue;
      texts.push({ text: d.text, x: d.x, y: d.y, z: d.z, dx: d.dx ?? 1, dz: d.dz ?? 0, fill: LABEL_FILL[d.kind ?? 'station'] ?? '#474d52', size: LABEL_H });
    }

    const lineCoords: number[] = (payload.lines ?? []).slice();

    // The compass belongs to a bridge, not to a bare scene. An EMPTY payload means "clear the
    // label layer", and that has to include the north arrow — otherwise a caller showing a single
    // component on its own (which pushes no labels) is left with a compass and its leader line
    // floating beside the part.
    const hasLabelContent = (payload.labels?.length ?? 0) > 0 || (payload.lines?.length ?? 0) > 0;
    if (hasLabelContent) {
      const north = buildNorthLines(this.bridge, payload.north);
      if (north) { for (const v of north.coords) lineCoords.push(v); texts.push(north.n); }
    }

    const textMesh = buildTextMesh(texts);
    if (textMesh) g.add(textMesh);

    if (lineCoords.length >= 6) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lineCoords), 3));
      const mat = new THREE.LineBasicMaterial({ color: 0x9aa0a6, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false });
      const seg = new THREE.LineSegments(geom, mat);
      seg.renderOrder = 997;
      g.add(seg);
    }

    g.visible = this.labelsVisible;
    this.labelsGroup = g;
    this.viewport.scene.add(g);
    if (this.ghost) this.hideOverlays();   // a component FE Mesh view is up
    this.viewport.invalidate();
  }

  setText(on: boolean): void {
    this.labelsVisible = on;
    // Under a component FE Mesh view the labels stay hidden; the toggle applies on the way out.
    if (this.labelsGroup && this.ghost && this.overlayVisibility) this.overlayVisibility.set(this.labelsGroup, on);
    else if (this.labelsGroup) this.labelsGroup.visible = on;
    this.viewport.invalidate();
  }

  setDatum(on: boolean, json: string): void {
    if (this.datumGroup) { this.viewport.scene.remove(this.datumGroup); disposeLabels(this.datumGroup); this.datumGroup = null; }
    if (!on) { this.viewport.invalidate(); return; }

    let d: DatumPayload = {};
    try { d = JSON.parse(json); } catch { return; }

    const g = new THREE.Group();
    g.name = 'BridgeDatum';

    let minX = -20, maxX = 20, minZ = -20, maxZ = 20;
    const b = this.bridge.getBounds();
    if (b && !b.isEmpty()) { const m = 6; minX = b.min.x - m; maxX = b.max.x + m; minZ = b.min.z - m; maxZ = b.max.z + m; }
    const w = maxX - minX, dpt = maxZ - minZ, cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;

    const deckY = d.deckY ?? 0, groundY = d.groundY ?? 0;
    const dirx = d.dir?.[0] ?? 1, dirz = d.dir?.[1] ?? 0;
    const texts: TextItem[] = [];
    const lineCoords: number[] = [];

    const plane = (y: number, color: number): void => {
      const geo = new THREE.PlaneGeometry(w, dpt);
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.1, side: THREE.DoubleSide, depthWrite: false });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(cx, y, cz);
      mesh.renderOrder = 990;
      g.add(mesh);
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo),
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.55 }));
      edges.rotation.x = -Math.PI / 2;
      edges.position.set(cx, y, cz);
      edges.renderOrder = 991;
      g.add(edges);
    };
    plane(deckY, 0x2a6fd6);
    plane(groundY, 0x8a6d3b);

    const perpx = -dirz, perpz = dirx;
    const halfPerp = (Math.abs(perpx) * w + Math.abs(perpz) * dpt) / 2 + 2.5;
    const halfLen = (Math.abs(dirx) * w + Math.abs(dirz) * dpt) / 2;
    const lx = cx + perpx * halfPerp - dirx * halfLen;
    const lz = cz + perpz * halfPerp - dirz * halfLen;
    if (d.deckLabel) texts.push({ text: d.deckLabel, x: lx, y: deckY + 0.05, z: lz, dx: dirx, dz: dirz, fill: '#2a6fd6', size: LABEL_H });
    if (d.groundLabel) texts.push({ text: d.groundLabel, x: lx, y: groundY + 0.05, z: lz, dx: dirx, dz: dirz, fill: '#8a6d3b', size: LABEL_H });

    const s = d.start ?? [0, deckY, 0];
    lineCoords.push(s[0], groundY, s[2], s[0], deckY, s[2]);

    const alen = (b && !b.isEmpty()) ? Math.max(4, (b.max.x - b.min.x + b.max.z - b.min.z) * 0.06) : 6;
    const ex = s[0] + dirx * alen, ez = s[2] + dirz * alen;
    lineCoords.push(s[0], deckY, s[2], ex, deckY, ez);
    const bx = -dirx, bz = -dirz, px = -dirz, pz = dirx, hl = alen * 0.2, hw = alen * 0.09;
    lineCoords.push(ex, deckY, ez, ex + bx * hl + px * hw, deckY, ez + bz * hl + pz * hw);
    lineCoords.push(ex, deckY, ez, ex + bx * hl - px * hw, deckY, ez + bz * hl - pz * hw);

    if (d.stationLabel) texts.push({ text: d.stationLabel, x: s[0], y: deckY + 1.3, z: s[2], dx: dirx, dz: dirz, fill: '#363b3f', size: LABEL_H });
    if (d.bearingLabel) texts.push({ text: d.bearingLabel, x: ex, y: deckY + 0.5, z: ez, dx: dirx, dz: dirz, fill: '#363b3f', size: LABEL_H });

    const textMesh = buildTextMesh(texts);
    if (textMesh) g.add(textMesh);

    if (lineCoords.length >= 6) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lineCoords), 3));
      const seg = new THREE.LineSegments(geom,
        new THREE.LineBasicMaterial({ color: 0x555b60, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false }));
      seg.renderOrder = 996;
      g.add(seg);
    }

    this.datumGroup = g;
    this.viewport.scene.add(g);
    if (this.ghost) this.hideOverlays();   // a component FE Mesh view is up
    this.viewport.invalidate();
  }

  /**
   * A JPEG data URL of the WHOLE bridge from the isometric home angle, for the
   * Recent Projects thumbnail (ProjectThumbnails.cs writes it beside the
   * .bdbx's MRU entry when the project is saved).
   *
   * JPEG, not PNG: the scene background is opaque so there is no alpha to lose,
   * and a 3D render is photographic — PNG would be several hundred KB of it,
   * which then crosses the interop boundary and sits in a Blazor render diff
   * ten times over. At this size the compression artefacts are invisible.
   *
   * Explicitly not a screenshot of the current view: it frames the bridge's own
   * bounds from ISO_DIR through a throwaway camera, so a user zoomed into one
   * pier cap still gets a thumbnail of the bridge. Frames the BRIDGE rather
   * than `currentBounds()` — an FE results mesh happening to be the active view
   * is not what the project should be recognised by. Returns '' when there is
   * no geometry to shoot.
   */
  captureThumbnail(width: number, height: number): string {
    const box = this.bridge.hasContent ? this.bridge.getBounds() : null;
    if (!box || box.isEmpty()) return '';
    this.meshGizmo?.setVisible(false);
    try {
      return this.viewport.CaptureImage(
        box, new THREE.Vector3(...ISO_DIR), width, height, 1.12, 'image/jpeg', 0.85);
    } finally {
      this.meshGizmo?.setVisible(true);
    }
  }

  /** Put the move / rotate / scale gizmo on the custom mesh drawn as part `partId`. */
  editMesh(partId: string, pose: MeshPose, host: PickRef, mode?: GizmoMode): boolean {
    if (this.feActive || !this.bridge.root.visible) { this.meshGizmo?.detach(); return false; }
    this.meshGizmo ??= new MeshGizmo(this.viewport, this.bridge);
    if (mode) this.meshGizmo.setMode(mode);
    return this.meshGizmo.attach(partId, pose, host);
  }

  endMeshEdit(): void { this.meshGizmo?.detach(); }

  resize(): void { this.viewport.OnContainerResize(); }
  setRendering(on: boolean): void { this.viewport.setRenderingEnabled(on); }

  dispose(): void {
    this.meshGizmo?.dispose();
    this.meshGizmo = null;
    this.clearMarkers();
    this.stopFEAnimation();
    this.themeOff?.();
    this.hud.dispose();
    this.colorScale.dispose();
    this.clip.dispose();
    this.clearMapGrid();
    this.bridge.clear();
    this.fe.clear();
    this.viewport.Dispose();
    this.grid = null;
    this.axes = null;
    this.labelsGroup = null;
    this.datumGroup = null;
    this.themeOff = null;
    this.darkBackground = null;
    this.feActive = false;
  }

  isReady(): boolean {
    return document.getElementById(this.hostId) !== null;
  }
}

// =============================================================
//  Instance registry + exported (id-keyed) 3D API
// =============================================================
const views = new Map<string, Instance>();

/** Create (or recreate) the viewport inside the element with the given id.
 *  Safe to call repeatedly — tears down any previous instance for that id first. */
export function init(id: string): void {
  const el = document.getElementById(id);
  if (!el) {
    console.error('[BridgeViewport] container element not found:', id);
    return;
  }
  views.get(id)?.dispose();
  views.set(id, new Instance(el, id));
}

/** Tear down + forget the viewport for the given id. */
export function dispose(id: string): void {
  const v = views.get(id);
  if (v) { v.dispose(); views.delete(id); }
}

/** True once a viewport exists for the id (Blazor can poll before loading). */
export function isReady(id: string): boolean {
  const v = views.get(id);
  return v != null && v.isReady();
}

export function load(id: string, json: string, keepView = false): void { views.get(id)?.load(json, keepView); }
export function loadNotice(id: string): string { return views.get(id)?.loadNotice() ?? ''; }
export function loadLandXml(id: string, xml: string, fileName?: string, optionsJson?: string): string {
  return views.get(id)?.loadLandXml(xml, fileName, optionsJson) ?? '{"error":"The 3D view is not ready."}';
}
export function setMapDisplay(id: string, optionsJson: string): string {
  return views.get(id)?.setMapDisplay(optionsJson) ?? '{"error":"The 3D view is not ready."}';
}
export function setTerrainDisplay(id: string, optionsJson: string): string {
  return views.get(id)?.setTerrainDisplay(optionsJson) ?? '';
}
export function zoomAll(id: string): void { views.get(id)?.zoomAll(); }
export function zoomIn(id: string): void { views.get(id)?.zoomIn(); }
export function zoomOut(id: string): void { views.get(id)?.zoomOut(); }
export function zoomWindow(id: string): void { views.get(id)?.zoomWindow(); }
export function setProjection(id: string, ortho: boolean): void { views.get(id)?.setProjection(ortho); }
export function setOrthoZoomSpeed(id: string, speed: number): void { views.get(id)?.setOrthoZoomSpeed(speed); }
export function setRotateMode(id: string, mode: string): void { views.get(id)?.setRotateMode(mode); }
export function setView(id: string, name: ViewName): void { views.get(id)?.setView(name); }
// TEMP debug helper — orbit the view by hand to match a reference screenshot, then
// call BridgeViewport.debugCamera('modeler-canvas') in DevTools console to read off
// the exact azimuth/elevation to bake into setView's `iso` direction. Remove once
// the default iso angle is dialed in.
export function debugCamera(id: string): { azimuthDeg: number; elevationDeg: number } | undefined {
  const v = views.get(id);
  if (!v) return undefined;
  const azimuthDeg = THREE.MathUtils.radToDeg(v.viewport.controls.azimuthAngle);
  const elevationDeg = 90 - THREE.MathUtils.radToDeg(v.viewport.controls.polarAngle);
  console.log('[BridgeViewport] camera azimuth=', azimuthDeg.toFixed(1), 'deg, elevation=', elevationDeg.toFixed(1), 'deg');
  return { azimuthDeg, elevationDeg };
}
export function setShadows(id: string, on: boolean): void { views.get(id)?.setShadows(on); }
export function setGrid(id: string, on: boolean): void { views.get(id)?.setGrid(on); }
export function setAxis(id: string, on: boolean, length?: number): void { views.get(id)?.setAxis(on, length); }
export function setText(id: string, on: boolean): void { views.get(id)?.setText(on); }
export function select(id: string, ids: string[]): void { views.get(id)?.select(ids); }
export function setIsolate(id: string, ids: string[] | null): void { views.get(id)?.setIsolate(ids); }
export function setSelectionStyle(id: string, style: string): void { views.get(id)?.setSelectionStyle(style); }
export function setHullLook(id: string, colour?: string, weight?: number): void { views.get(id)?.setHullLook(colour, weight); }
export function setOutlineAll(id: string, on: boolean): void { views.get(id)?.setOutlineAll(on); }
export function setOutlineParts(id: string, hull: boolean, creases: boolean, angle?: number): void { views.get(id)?.setOutlineParts(hull, creases, angle); }
export function setIntersectionLines(id: string, on: boolean, angle?: number, width?: number): void { views.get(id)?.setIntersectionLines(on, angle, width); }
export function setTransparency(id: string, value: number): void { views.get(id)?.setTransparency(value); }
export function setLabels(id: string, json: string): void { views.get(id)?.setLabels(json); }
export function setDatum(id: string, on: boolean, json: string): void { views.get(id)?.setDatum(on, json); }
export function captureThumbnail(id: string, width: number, height: number): string {
  return views.get(id)?.captureThumbnail(width, height) ?? '';
}
export function resize(id: string): void { views.get(id)?.resize(); }

/** Stats of the last {@link exportShareMesh}, for the host to word its message. */
let lastShareStats: ShareExportStats | null = null;

/**
 * The bridge as drawn, as a gzipped meshopt glTF for the phone / VR / AR share
 * viewer (io/ShareExport.ts). `id` null picks the largest 3D view on the page
 * that has a model - the host's share button has no view id of its own.
 */
export async function exportShareMesh(id: string | null, name: string): Promise<Uint8Array> {
  let pick: Instance | undefined = id ? views.get(id) : undefined;
  if (!pick) {
    let best = -1;
    for (const v of views.values()) {
      const el = document.getElementById(v.hostId);
      if (!el || !el.isConnected || !v.bridge.hasContent) continue;
      const area = el.clientWidth * el.clientHeight;
      if (area > best) { best = area; pick = v; }
    }
  }
  if (!pick) throw new Error('Open the model in the 3D view first.');
  const { bytes, stats } = await writeShareMesh(pick.bridge.root, pick.viewport.camera, name);
  lastShareStats = stats;
  return bytes;
}

export function shareMeshStats(): ShareExportStats | null { return lastShareStats; }

/** A QR code for `text`, as an SVG string. */
export function qrSvg(text: string): string { return makeQrSvg(text); }

/**
 * A 3D file (IFC, glTF/GLB, OBJ, STL, PLY, FBX, DAE, 3MF, 3DS, AMF, USDZ, three.js JSON)
 * read into colour groups for a custom mesh in the bridge model, as a JSON string —
 * see io/MeshImport.ts. Needs no 3D view: the host can call it at any time.
 */
export async function parseMesh(fileName: string, data: Uint8Array): Promise<string> {
  return JSON.stringify(await readMeshFile(fileName, data));
}

/** The extensions parseMesh reads. */
export function meshFormats(): string[] { return [...MESH_FORMATS]; }

export function setRendering(id: string, on: boolean): void { views.get(id)?.setRendering(on); }

export function loadFEMesh(id: string, json: string, keepView?: boolean, ghostIds?: string[] | null): void { views.get(id)?.loadFEMesh(json, !!keepView, ghostIds); }
export function loadFEAnimationMesh(id: string, json: string): void { views.get(id)?.loadFEAnimationMesh(json); }
export function playFEScaleAnimation(id: string, fieldJson: string, modal: boolean, baseScale: number, intervalMs: number): void {
  views.get(id)?.playFEScaleAnimation(fieldJson, modal, baseScale, intervalMs);
}
export function playFEStepAnimation(id: string, fieldsJson: string, scale: number, intervalMs: number): void {
  views.get(id)?.playFEStepAnimation(fieldsJson, scale, intervalMs);
}
export function setFEModelOverlay(id: string, on: boolean): void { views.get(id)?.setFEModelOverlay(on); }
export function setFEModelOverlayOpacity(id: string, opacity: number): void { views.get(id)?.setFEModelOverlayOpacity(opacity); }
export function partsCenter(id: string, ids: string[]): number[] | null { return views.get(id)?.partsCenter(ids) ?? null; }
export function stopFEAnimation(id: string): void { views.get(id)?.stopFEAnimation(); }
export function clearFEMesh(id: string): void { views.get(id)?.clearFEMesh(); }
export function setFEVisible(id: string, type: string, on: boolean): void { views.get(id)?.setFEVisible(type, on); }
export function setFEDepthTest(id: string, disabled: boolean): void { views.get(id)?.setFEDepthTest(disabled); }
export function setFEExtrude(id: string, on: boolean): void { views.get(id)?.setFEExtrude(on); }
export function setFEShellOpacity(id: string, opacity: number): void { views.get(id)?.setFEShellOpacity(opacity); }
export function setAntialiasMode(id: string, mode: string): void { views.get(id)?.setAntialiasMode(mode); }
export function setColorScale(id: string, min: number, max: number, title: string): void { views.get(id)?.setColorScale(min, max, title); }
export function clearColorScale(id: string): void { views.get(id)?.clearColorScale(); }
export function setDiagramLabelScale(id: string, k: number): void { views.get(id)?.setDiagramLabelScale(k); }
export function setReactionArrows(id: string, json: string): void { views.get(id)?.setReactionArrows(json); }
export function clearReactionArrows(id: string): void { views.get(id)?.clearReactionArrows(); }
export function setClipping(id: string, on: boolean): void { views.get(id)?.setClipping(on); }
export function registerClipCloseCallback(id: string, dotNetRef: PickRef): void { views.get(id)?.registerClipCloseCallback(dotNetRef); }
export function highlightFE(id: string, json: string): void { views.get(id)?.highlightFE(json); }
export function clearFEHighlight(id: string): void { views.get(id)?.clearFEHighlight(); }
export function setFEOverlay(id: string, json: string): void { views.get(id)?.setFEOverlay(json); }
export function clearFEOverlay(id: string): void { views.get(id)?.clearFEOverlay(); }
export function enableFePicking(id: string, dotNetRef: PickRef): void { views.get(id)?.enableFePicking(dotNetRef); }
export function enableBridgePicking(id: string, dotNetRef: PickRef): void { views.get(id)?.enableBridgePicking(dotNetRef); }
/** The last bridge click's hit — part id, point and normal in world space — or null on a miss. */
export function lastPick(id: string): { id: string; point: [number, number, number]; normal: [number, number, number] } | null { return views.get(id)?.lastPickHit ?? null; }
/** Host markers (Bridge Watch's defect pins); an empty list clears them. */
export function setMarkers(id: string, markers: MarkerSpec[]): void { views.get(id)?.setMarkers(markers); }
/** Clicks on a marker report 'OnMarkerPicked'(marker id) instead of picking the bridge. */
export function enableMarkerPicking(id: string, ref: PickRef): void { const v = views.get(id); if (v) v.markerPickRef = ref; }

export interface MarkerSpec {
  id: string;
  position: [number, number, number];
  normal?: [number, number, number];
  color: string;
  opacity?: number;
  /** Metres; 0.45 by default. */
  radius?: number;
  /** Lines of text on a card above the pin; the first is bold. */
  label?: string[];
}
export function setSelectMode(id: string, on: boolean): void { views.get(id)?.setSelectMode(on); }
/** Custom meshes: handles to move / rotate / scale part `partId` in the view; the host hears
 *  'OnCustomMeshTransformed'(partId, x, y, z, rotation, scale) — model mm and degrees — on release. */
export function editMesh(id: string, partId: string, pose: MeshPose, host: PickRef, mode?: GizmoMode): boolean {
  return views.get(id)?.editMesh(partId, pose, host, mode) ?? false;
}
export function endMeshEdit(id: string): void { views.get(id)?.endMeshEdit(); }

/** The model overlay's edges, a little stronger than its faces so the shape still
 *  reads when the faces are nearly clear. */
function overlayEdgeOpacity(surface: number): number {
  return Math.min(1, 0.1 + surface * 1.1);
}

/** Free a detached group's geometry and materials. */
function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    m.geometry?.dispose();
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    for (const mat of mats) (mat as THREE.Material | undefined)?.dispose();
  });
}

/** Short form of a LandXML linear unit, for the legend title. */
function unitAbbrev(unit: string): string {
  switch ((unit ?? '').toLowerCase()) {
    case 'millimeter': case 'millimetre': return 'mm';
    case 'centimeter': case 'centimetre': return 'cm';
    case 'meter': case 'metre': return 'm';
    case 'kilometer': case 'kilometre': return 'km';
    case 'foot': return 'ft';
    case 'ussurveyfoot': return 'USft';
    case 'inch': return 'in';
    case 'mile': return 'mi';
    default: return unit || '';
  }
}

/** Read terrain options off the wire, falling back per-field. A malformed or
 *  absent payload must leave the scene drawn, not throw across interop. */
function parseTerrainOptions(json: string | undefined, base: TerrainDisplay): TerrainDisplay {
  if (!json) return { ...base };
  try {
    const o = JSON.parse(json) as Partial<TerrainDisplay>;
    return {
      elevation: typeof o.elevation === 'boolean' ? o.elevation : base.elevation,
      wireframe: typeof o.wireframe === 'boolean' ? o.wireframe : base.wireframe,
      subdivide: Number.isFinite(o.subdivide as number)
        ? Math.max(0, Math.min(3, Math.round(o.subdivide as number)))
        : base.subdivide,
      smooth: typeof o.smooth === 'boolean' ? o.smooth : base.smooth,
      vegetation: Number.isFinite(o.vegetation as number)
        ? Math.max(0, Math.min(1, o.vegetation as number))
        : base.vegetation,
    };
  } catch {
    return { ...base };
  }
}

/** Import Map's options are the terrain ones; same reader. */
const parseDisplayOptions = parseTerrainOptions;

// =============================================================
//  Shared pure helpers (used by Instance methods)
// =============================================================
function disposeHelper(o: THREE.Object3D): void {
  o.traverse((c) => {
    const h = c as unknown as { geometry?: { dispose(): void }; material?: THREE.Material | THREE.Material[] };
    h.geometry?.dispose();
    const m = h.material;
    if (Array.isArray(m)) m.forEach((x) => x.dispose());
    else m?.dispose();
  });
}

/** North arrow, the WinUI template (BridgeLayoutObject.GetNorthArrowModel, L = 1 m): a
 *  shaft from -3L to 1.25L, a closed arrowhead notched at 1.25L with its tip at 2.5L, a
 *  circle of radius L at 3.5L and the "N" inside it, all along the true-north direction.
 *  Placed as there too: at the bridge start, beside the deck's left edge (7 m clear of it),
 *  at deck level - outside the deck footprint, so a top view never hides it.
 *
 *  `north` is the unit direction of true north in viewer x/z, as exported with the labels
 *  (BridgeGeometryExporter.ExportLabelsJson). The bridge is always drawn leaving the origin
 *  along +X, so the Start Bearing shows up here - as where north points relative to the
 *  bridge - rather than as a rotation of the model. Absent (an older payload), north is -Z,
 *  which is the bridge heading due east. */
function buildNorthLines(bridge: BridgeScene, north?: [number, number]): { coords: number[]; n: TextItem } {
  const L = 1;
  const b = bridge.getBounds();
  let oy = 0, oz = -7 * L;
  if (b && !b.isEmpty()) {
    oy = b.max.y;                 // deck top
    oz = b.min.z - 7 * L;         // model +Y (left of the alignment) is viewer -Z
  }

  let nx = 0, nz = -1;
  if (north && north.length >= 2 && Number.isFinite(north[0]) && Number.isFinite(north[1])) {
    const len = Math.hypot(north[0], north[1]);
    if (len > 1e-9) { nx = north[0] / len; nz = north[1] / len; }
  }
  const px = -nz, pz = nx;      // across the shaft, in the ground plane
  const at = (along: number, across: number): [number, number] =>
    [nx * along + px * across, oz + nz * along + pz * across];

  const seg = (a0: number, c0: number, a1: number, c1: number): number[] => {
    const [x0, z0] = at(a0, c0), [x1, z1] = at(a1, c1);
    return [x0, oy, z0, x1, oy, z1];
  };
  const coords: number[] = [
    ...seg(-3 * L, 0, 1.25 * L, 0),                 // shaft
    ...seg(1.25 * L, 0, L, 0.25 * L),               // arrowhead: notch -> barb
    ...seg(L, 0.25 * L, 2.5 * L, 0),                //            barb -> tip
    ...seg(1.25 * L, 0, L, -0.25 * L),
    ...seg(L, -0.25 * L, 2.5 * L, 0),
  ];
  const r = L, c = 3.5 * L, segs = 90;
  for (let i = 0; i < segs; i++) {
    const t0 = (i / segs) * Math.PI * 2, t1 = ((i + 1) / segs) * Math.PI * 2;
    coords.push(...seg(c + r * Math.cos(t0), r * Math.sin(t0), c + r * Math.cos(t1), r * Math.sin(t1)));
  }
  const [cx, cz] = at(c, 0);
  return { coords, n: { text: 'N', x: cx, y: oy, z: cz, dx: px, dz: pz, fill: '#474d52', size: L } };
}

/** Build ALL labels as real FLAT text geometry (ShapeGeometry) merged into ONE mesh. */
/** One line of text as a sprite: always faces the camera and keeps its size on
 *  screen (sizeAttenuation off), drawn over everything so a see-through solid
 *  round the diagram never hides a value. Dark text on a light halo, which reads
 *  on the diagram's saturated colours and on the background alike. */
function makeTextSprite(text: string): THREE.Sprite {
  const px = 44;
  const pad = 10;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const font = `600 ${px}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(text).width) + pad * 2;
  const h = px + pad * 2;
  canvas.width = w;
  canvas.height = h;
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 10;
  ctx.strokeStyle = 'rgba(255,255,255,0.92)';
  ctx.strokeText(text, w / 2, h / 2);
  ctx.fillStyle = '#1f2328';
  ctx.fillText(text, w / 2, h / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, sizeAttenuation: false, transparent: true });
  const sprite = new THREE.Sprite(mat);
  const height = 0.02;                        // fraction of the view's height
  sprite.scale.set(height * (w / h), height, 1);
  sprite.renderOrder = 1000;
  return sprite;
}

/** A marker's text as a sprite card: light panel with a stripe in the marker's colour,
 *  the first line bold, the rest lighter. 12 px text on screen, drawn at twice the device
 *  resolution so it stays crisp; drawn over everything so a pin behind a girder still says
 *  what it is. Its screen height (userData.hPx) and aspect are kept for setMarkers' restrike. */
const MARKER_TEXT_PX = 12;
function makeMarkerCard(lines: string[], color: string, opacity: number): THREE.Sprite {
  const k = 2 * Math.max(1, window.devicePixelRatio || 1);   // canvas pixels per screen pixel
  const px = MARKER_TEXT_PX * k;
  const lineH = Math.round(px * 1.3);
  const pad = Math.round(4 * k);
  const stripe = Math.round(3 * k);
  const fonts = lines.map((_, i) => `${i === 0 ? 700 : 500} ${px}px "Helvetica Neue", Helvetica, Arial, sans-serif`);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  let textW = 0;
  lines.forEach((t, i) => { ctx.font = fonts[i]; textW = Math.max(textW, ctx.measureText(t).width); });
  const w = Math.ceil(textW) + stripe + pad * 3;
  const h = lineH * lines.length + pad * 2;
  canvas.width = w;
  canvas.height = h;

  ctx.beginPath();
  ctx.roundRect(k / 2, k / 2, w - k, h - k, 3 * k);
  ctx.fillStyle = 'rgba(255,255,255,0.94)';
  ctx.fill();
  ctx.lineWidth = k;
  ctx.strokeStyle = 'rgba(31,35,40,0.25)';
  ctx.stroke();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, stripe + 2, h);
  ctx.restore();

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  lines.forEach((t, i) => {
    ctx.font = fonts[i];
    ctx.fillStyle = i === 0 ? '#1f2328' : '#57606a';
    ctx.fillText(t, stripe + pad * 2, pad + lineH * (i + 0.5));
  });

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, sizeAttenuation: false, transparent: true, opacity });
  const sprite = new THREE.Sprite(mat);
  sprite.userData.hPx = h / k;
  sprite.userData.aspect = w / h;
  sprite.renderOrder = 1001;
  return sprite;
}

function buildTextMesh(items: TextItem[]): THREE.Mesh | null {
  if (!items.length) return null;

  const up = new THREE.Vector3(0, 1, 0);
  const xAxis = new THREE.Vector3(), yAxis = new THREE.Vector3();
  const basis = new THREE.Matrix4();
  const geos: THREE.BufferGeometry[] = [];

  for (const it of items) {
    const shapes = LABEL_FONT.generateShapes(it.text, it.size);
    const geo: THREE.BufferGeometry = new THREE.ShapeGeometry(shapes, 4);

    geo.computeBoundingBox();
    const bb = geo.boundingBox!;
    geo.translate(-(bb.min.x + bb.max.x) / 2, -(bb.min.y + bb.max.y) / 2, 0);

    xAxis.set(it.dx, 0, it.dz);
    if (xAxis.lengthSq() < 1e-9) xAxis.set(1, 0, 0); else xAxis.normalize();
    yAxis.set(xAxis.z, 0, -xAxis.x);
    basis.makeBasis(xAxis, yAxis, up);
    geo.applyMatrix4(basis);
    geo.translate(it.x, it.y, it.z);

    const col = new THREE.Color(it.fill);
    const n = geo.attributes.position.count;
    const colors = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { colors[i * 3] = col.r; colors[i * 3 + 1] = col.g; colors[i * 3 + 2] = col.b; }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geos.push(geo);
  }

  const merged = mergeGeometries(geos, false)!;
  geos.forEach((g) => g.dispose());
  const mat = new THREE.MeshBasicMaterial({
    vertexColors: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(merged, mat);
  mesh.renderOrder = 999;
  return mesh;
}

function disposeLabels(g: THREE.Group): void {
  g.traverse((o) => {
    const any = o as THREE.Mesh & { geometry?: THREE.BufferGeometry; material?: THREE.Material | THREE.Material[] };
    any.geometry?.dispose?.();
    const mats = Array.isArray(any.material) ? any.material : any.material ? [any.material] : [];
    for (const m of mats) { (m as THREE.MeshBasicMaterial).map?.dispose(); m.dispose(); }
  });
}

// =============================================================
//  2D canvas previews (property-panel mesh / chart / key-plan)
//  Independent of the 3D viewports; already keyed by host element id.
// =============================================================
const canvas2d = new Map<string, Canvas2DRenderer>();

export function mount2D(containerId: string, json: string | DrawingPage): void {
  const el = document.getElementById(containerId);
  if (!el) {
    console.error('[BridgeViewport] mount2D: element not found:', containerId);
    return;
  }
  let page: DrawingPage;
  try {
    page = typeof json === 'string' ? JSON.parse(json) : json;
  } catch (e) {
    console.error('[BridgeViewport] mount2D: failed to parse page json', e);
    return;
  }
  let r = canvas2d.get(containerId);
  if (r && r.hostElement !== el) {
    r.Dispose();
    canvas2d.delete(containerId);
    r = undefined;
  }
  if (!r) {
    r = new Canvas2DRenderer(el);
    r.showGrid = false;
    r.showMinorGrid = false;
    r.showAxis = false;
    r.showRuler = false;
    canvas2d.set(containerId, r);
  }
  r.SetPage(page);
}

/**
 * Mount a drawing that was authored as SVG (SectionSvgBuilder's dimensioned
 * component sections, PageSvgRenderer's engine pages) on the same 2D canvas
 * every other drawing in the Property panel uses.
 *
 * The SVG is parsed into a DrawingPage (io/SvgImport) rather than dropped in as
 * markup or as an image, so the section arrives as real geometry: pan, the
 * cursor-anchored wheel, fit-to-window and PNG / DXF / SVG export all come from
 * the renderer, and lines stay one pixel at any magnification.
 *
 * Returns false when the markup is not one of ours or yields nothing to draw —
 * the caller shows the SVG inline instead, which is what it did before.
 */
export function mountSvg2D(containerId: string, svg: string): boolean {
  const page = svgToPage(svg);
  if (!page) {
    console.warn('[BridgeViewport] mountSvg2D: nothing to draw for', containerId);
    return false;
  }
  mount2D(containerId, page);
  return true;
}

/** As mountSvg2D, but keeps the current pan / zoom — for a redraw of the same
 *  drawing with different options (a dimension or unit toggle), which must not
 *  throw away the view the reader had scrolled to. */
export function updateSvg2D(containerId: string, svg: string): boolean {
  const page = svgToPage(svg);
  if (!page) return false;
  const r = canvas2d.get(containerId);
  if (!r || r.hostElement !== document.getElementById(containerId)) {
    return mountSvg2D(containerId, svg);
  }
  r.SetPageKeepView(page);
  return true;
}

export function update2D(containerId: string, json: string | DrawingPage): void {
  const r = canvas2d.get(containerId);
  if (!r) {
    mount2D(containerId, json);
    return;
  }
  let page: DrawingPage;
  try {
    page = typeof json === 'string' ? JSON.parse(json) : json;
  } catch (e) {
    console.error('[BridgeViewport] update2D: failed to parse page json', e);
    return;
  }
  r.SetPageKeepView(page);
}

/**
 * Let the reader lay a COMPOSED SHEET out by hand: drag a view to another place
 * on the paper, right-click one for its own menu.
 *
 * Only a composed sheet has views to move (DrawingPage.views), so this is a
 * no-op on every other drawing — but it is still the HOST that decides, because
 * only the host knows whether it has anywhere to put the answer.
 *
 * `host` is a .NET object reference with two methods:
 *
 *   OnSheetViewMoved(id, dx, dy)                       — a view was dropped, dx/dy in page mm
 *   OnSheetViewMenu(id, caption, scale, pinned, moved, x, y) — a view was right-clicked
 *
 * The renderer keeps a dropped view under the cursor until the host sends the
 * re-composed page back, so nothing flickers while that round trip runs.
 */
export function setSheetEditing(containerId: string, host: SheetEditHost | null): void {
  const r = canvas2d.get(containerId);
  if (!r) return;

  r.sheetEditable = !!host;

  if (!host) {
    r.onViewMove = null;
    r.onViewMenu = null;
    return;
  }

  r.onViewMove = (id, dx, dy) => {
    // Fire and forget: the host answers by re-exporting the sheet, which comes
    // back through update2D like any other redraw.
    host.invokeMethodAsync('OnSheetViewMoved', id, dx, dy).catch(() => {
      /* the panel was torn down mid-drag */
    });
  };

  r.onViewMenu = (view, clientX, clientY) => {
    host
      .invokeMethodAsync(
        'OnSheetViewMenu',
        view.id,
        view.caption ?? '',
        view.scale ?? null,
        !!view.pinned,
        !!view.moved,
        clientX,
        clientY,
      )
      .catch(() => {
        /* as above */
      });
  };
}

/** The .NET side of setSheetEditing — a DotNetObjectReference. */
interface SheetEditHost {
  invokeMethodAsync(method: string, ...args: unknown[]): Promise<unknown>;
}

/** Set the plain line's screen width, in pixels, on one 2D canvas — see
 *  Canvas2DRenderer.SetLineScale. 1 is the default CAD line. */
export function setLineScale2D(containerId: string, scale: number): void {
  canvas2d.get(containerId)?.SetLineScale(scale);
}

export function zoom2D(containerId: string, dir: 'in' | 'out' | 'fit'): void {
  const r = canvas2d.get(containerId);
  if (!r) return;
  if (dir === 'in') r.ZoomIn();
  else if (dir === 'out') r.ZoomOut();
  else r.ZoomAll();
}

/**
 * A drawing page as inline SVG, with no canvas behind it.
 *
 * The Property panel renders its 2D drawings this way rather than mounting a
 * Canvas2DRenderer per card: a page of SVG is a handful of DOM nodes that the
 * host can zoom with CSS, needs no WebGL context, no resize observer and no
 * disposal, and survives a panel being torn off into another window. The canvas
 * renderer is still what the Drawing tab and the export paths use, and it stays
 * the only thing that can pan or produce PNG.
 *
 * A DrawingPage IS the parsed JSON (see mount2D), so this needs no renderer and
 * no element — which is the whole reason it can be called before layout.
 */
export function render2DSvg(json: string): string {
  try {
    const page: DrawingPage = typeof json === 'string' ? JSON.parse(json) : json;
    return pageToSvg(page, { inline: true });
  } catch (e) {
    console.error('[BridgeViewport] render2DSvg failed', e);
    return '';
  }
}

/**
 * The 2D preview at `containerId` as an exportable file, or '' when there is no
 * renderer / nothing drawn. The caller (PropertyCard's export menu) writes the
 * result to the path the user picks.
 *
 *   png — base64 of the CURRENT view: pan, zoom, grid, rulers and theme as seen,
 *         captured at 3x the device pixel ratio so it holds up in a report.
 *   svg — the whole drawing as vectors, on the sheet colour of the current
 *         theme, so it can be scaled or re-styled downstream.
 *   dxf — the whole drawing as CAD geometry (R12, millimetres), on GEOMETRY,
 *         TEXT and DIMENSIONS layers, its lettering pointing at a STYLE table
 *         and its dimensions written as real DIMENSION entities.
 *
 * The vector formats deliberately take the whole drawing rather than the
 * visible window: a section clipped to whatever the user happened to have
 * scrolled into view is not something CAD or a report can use.
 */
export function export2D(containerId: string, format: 'png' | 'svg' | 'dxf'): string {
  const r = canvas2d.get(containerId);
  if (!r) {
    console.error('[BridgeViewport] export2D: no 2D renderer for', containerId);
    return '';
  }
  try {
    if (format === 'png') {
      const url = r.ToPngDataUrl(3);
      // Hand back the payload only — .NET base64-decodes it straight to bytes.
      const comma = url.indexOf(',');
      return comma >= 0 ? url.slice(comma + 1) : '';
    }
    const page = r.GetPage();
    if (!page) return '';
    return format === 'svg' ? pageToSvg(page, { dark: r.isDark }) : pageToDxf(page);
  } catch (e) {
    console.error('[BridgeViewport] export2D failed', e);
    return '';
  }
}

/**
 * Hand a file to the browser as a download. Only the WASM head uses this — the
 * desktop head has a native Save As picker and writes the bytes itself (see
 * Canvas2DFileExport) — but it lives here because the payload is already in JS.
 *
 * `base64` says how to read `payload`: true for binary written as base64 (PNG),
 * false for text (SVG, DXF).
 */
export function downloadFile(
  fileName: string,
  mime: string,
  payload: string,
  base64: boolean,
): void {
  let blob: Blob;
  if (base64) {
    const bin = atob(payload);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    blob = new Blob([bytes], { type: mime });
  } else {
    blob = new Blob([payload], { type: `${mime};charset=utf-8` });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the navigation a beat to start before the blob is dropped.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function unmount2D(containerId: string): void {
  const r = canvas2d.get(containerId);
  if (r) {
    r.Dispose();
    canvas2d.delete(containerId);
  }
}

// =============================================================
//  Overlay anchoring (expanded 2D preview over the 3D view pane)
// =============================================================
const overlayAnchors = new Map<string, () => void>();

export function anchorOverlay(overlayId: string, targetSelector: string): void {
  unanchorOverlay(overlayId);
  const overlay = document.getElementById(overlayId);
  const target = document.querySelector(targetSelector) as HTMLElement | null;
  if (!overlay || !target) return;

  const place = (): void => {
    const r = target.getBoundingClientRect();
    overlay.style.left = `${r.left}px`;
    overlay.style.top = `${r.top}px`;
    overlay.style.width = `${r.width}px`;
    overlay.style.height = `${r.height}px`;
  };
  place();

  const ro =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => place()) : null;
  ro?.observe(target);
  window.addEventListener('resize', place);

  overlayAnchors.set(overlayId, () => {
    ro?.disconnect();
    window.removeEventListener('resize', place);
  });
}

export function unanchorOverlay(overlayId: string): void {
  const dispose = overlayAnchors.get(overlayId);
  if (dispose) {
    dispose();
    overlayAnchors.delete(overlayId);
  }
}
